<template>
  <!-- ==============================================================
       GuideTourWidget — LIVE walkthrough player. Two modes:

       • GUIDED (default when a recorded tour exists): the page stays
         live and clickable. Each step spotlights the control + any
         admin-marked "affected" sections, plays the admin's recorded
         voice, and advances when the CUSTOMER clicks the highlighted
         control (or hits Next). Because the customer drives their own
         page + data, it always matches what's on screen.

       • AUTO (built-in fallback script): dims + blocks the page and an
         animated cursor walks the steps itself, SIMULATING the cart/
         order writes so nothing is ordered.

       Launched via the root event 'start-guide-tour' (flow key). A
       recorded tour for that flow (localStorage 'jcs_tour_rec_<flow>',
       authored by GuideTourRecorder) takes precedence over the built-in.
       Mounted once in App.vue. CSS unscoped + namespaced (.gtour-*).
       ============================================================== -->
  <div class="guide-tour-widget" :class="{ 'gtour-guided': mode === 'guided' }" v-if="active || navNote">
    <!-- Standalone heads-up shown after a nav hand-off to a page that has no tour. -->
    <div class="gtour-navnote" v-if="navNote && !active">
      <span class="gtour-navnote-msg">{{ navNote.msg }}</span>
      <button v-if="navNote.back" type="button" class="gtour-navnote-back" @click="resumeNavBack">↩ Back to the {{ navNote.back.label }} tutorial</button>
      <button v-if="navNote.back" type="button" class="gtour-navnote-x" @click="navNote = ''" aria-label="Dismiss">✕</button>
    </div>
    <template v-if="active">
    <!-- AUTO mode blocks the page so the user just watches; GUIDED mode
         leaves the page interactive so the customer performs the steps. -->
    <div v-if="mode === 'auto'" class="gtour-catcher" @click.stop></div>

    <!-- GUIDED click-guard: tiles the page AROUND the spotlight hole so only the
         highlighted control (+ its menu/results) and the tour card are
         clickable — the customer can't click away or exit early; only ✕ exits. -->
    <div v-for="(g, i) in guardRects" :key="'g' + i" class="gtour-guard" :style="g"
         @click.stop.prevent @mousedown.stop.prevent @mouseup.stop.prevent></div>

    <!-- dim spotlight on the primary target -->
    <div class="gtour-spot" :style="spotStyle"></div>
    <!-- outline rings on each admin-marked "affected" section -->
    <div v-for="(r, i) in rings" :key="'r' + i" class="gtour-ring" :class="r.anim && r.anim !== 'none' ? 'gtour-anim-' + r.anim : ''" :style="r"></div>
    <!-- click-blockers over admin-masked elements (e.g. the cart icon) — they
         swallow clicks so the customer can't use them yet during this step -->
    <div v-for="(m, i) in maskRects" :key="'m' + i" class="gtour-mask" :style="m"
         @click.stop.prevent @mousedown.stop.prevent @mouseup.stop.prevent
         title="Not yet — follow the tour"></div>
    <!-- pulsing "click here" ring (guided, while waiting for the user) — hidden when
         the admin set an explicit Next, since clicking the highlight won't advance. -->
    <div v-if="awaitingClick && !(current && current.showNext)" class="gtour-clickhint" :style="spotStyle"></div>

    <!-- animated cursor (auto mode performs clicks; guided just hints) -->
    <div v-if="mode === 'auto'" class="gtour-cursor" :style="cursorStyle" :class="{ 'is-click': clicking }">
      <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
        <path fill="#fff" stroke="#c0392b" stroke-width="1.4" d="M5 3l14 8-6 1.5L16 19l-3 1-3-6.5L5 17z"/>
      </svg>
    </div>

    <!-- Red ring around the REAL cursor during a recorded Preview Tour, so the
         captured video shows where the presenter points/clicks. Positioned by a
         mousemove listener (direct style); above all overlays; never blocks clicks. -->
    <div v-if="recording" ref="recCursor" class="gtour-rec-cursor" :class="{ 'is-click': recClicking }"></div>

    <div class="gtour-toast" v-if="toast" :style="toastStyle">{{ toast }}</div>

    <div class="gtour-card" :style="cardStyle">
      <button type="button" class="gtour-card-close" @click="exit" title="Exit tour (Esc)" aria-label="Exit tour">✕</button>
      <div class="gtour-card-step">Step {{ stepNum }} of {{ steps.length }}</div>
      <div class="gtour-card-text" v-html="displayCaption"></div>
      <div class="gtour-card-cue" v-if="waitHint">
        ✏️ Make the change in the highlighted area — or click <b>Skip</b> to continue
      </div>
      <div class="gtour-card-cue" v-else-if="mode === 'guided' && current.showNext">
        👆 When you're ready, click <b>Next ›</b> to continue
      </div>
      <div class="gtour-card-cue" v-else-if="mode === 'guided' && awaitingClick && hasClickable">
        👆 Click the highlighted spot to continue
      </div>
      <!-- Input step: dynamic "you've narrowed it to <part>" callout after typing. -->
      <div class="gtour-card-result" v-if="resultCallout" v-html="resultCallout"></div>
      <div class="gtour-card-controls">
        <!-- ✕ (top-right) is the single, deliberate way to close the tour; the
             page is click-guarded so nothing else can exit it early. -->
        <button type="button" class="gtour-btn gtour-btn-ghost" v-if="stepIndex > 0" @click="prev">‹ Back</button>
        <span class="gtour-spacer"></span>
        <button type="button" class="gtour-btn gtour-btn-ghost" v-if="mode === 'auto'" @click="togglePause">
          {{ paused ? "Resume" : "Pause" }}
        </button>
        <!-- Next/Finish shows in AUTO mode always; in GUIDED mode when the admin
             enabled it for THIS step (current.showNext) OR it's an input/wait step
             — action steps advance by doing the highlighted action. The LAST step
             ALWAYS shows a Finish button so the customer can explicitly END the tour
             (and, during a recorded "Preview tour", stop + save the recording — the
             recorder uploads when the tour exits). ✕ / Esc also exit. -->
        <button type="button" class="gtour-btn" v-if="mode !== 'guided' || current.showNext || current.type === 'input' || current.wait || isLast" @click="next">
          {{ waitHint ? "Skip ›" : (isLast ? "Finish" : "Next ›") }}
        </button>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
var feapi = new FeApiService();

var BASE =
  (typeof process !== "undefined" && process.env && process.env.BASE_URL) || "/";
var TOUR_BASE = BASE + "videos/guides/tour/";

// Built-in fallback script (AUTO mode) — used only when no recorded tour
// exists for the flow. Mirrors the narration clips in /videos/guides/tour/.
var FLOWS = {
  order: [
    { audio: "s0", target: null, caption: "I'll walk you through placing a purchase order — live, on this page. Nothing will actually be ordered." },
    { audio: "s1", target: "#MachineSelect", caption: "First, under <b>Find Your Machine</b>, choose the machine you're ordering for." },
    { audio: "s2", target: "#configuredParts", action: "click", caption: "Flip on <b>Show wear parts only</b> to focus on the parts that wear out most." },
    { audio: "s3", target: "#WearAssemblySelect", caption: "Pick the <b>assembly</b> your part belongs to." },
    { audio: "s4", target: ".machine-image-wrap", caption: "Use the <b>parts diagram</b> — drag it and zoom in to read the balloon callouts." },
    { audio: "s5", target: ".inline-wear-part-item", action: "click", caption: "Each part has an <b>Item box</b>. Clicking a part highlights it and lets you add it." },
    { audio: "s6", target: ".inline-wear-part-item", action: "simulate", caption: "You'd set a <b>PO number</b> and <b>quantity</b>, then click <b>Add to Cart</b>. Nothing is added.", toast: "Add to Cart — demo only" },
    { audio: "s7", target: ".cart-pill", targetAlt: ".floating-pill-rail", caption: "Your <b>cart</b> keeps a running total of everything you've added." },
    { audio: "s8", target: null, caption: "From the cart you'd click <b>Place Order</b>. We'll stop here so nothing is submitted. That's the tour!" },
  ],
};

function estimateMs(text) {
  var words = String(text || "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(2600, Math.round((words / 2.4) * 1000) + 900);
}

export default {
  name: "GuideTourWidget",
  data() {
    return {
      active: false,
      mode: "auto",
      flowKey: "",
      steps: [],
      stepIndex: 0,
      paused: false,
      clicking: false,
      recording: false,    // recorded Preview Tour → show a red ring around the real cursor
      recClicking: false,  // ring pulses on mousedown (shows clicks in the recording)
      awaitingClick: false,
      waitHint: false,     // a {wait} in the narration is paused for a customer action
      toast: "",
      navNote: "",         // brief standalone heads-up (e.g. "no tutorial for this page") shown after a nav hand-off
      resultCallout: "",   // input step: filled-in "you found {part}" message after typing
      spotStyle: {},
      cursorStyle: {},
      cardStyle: {},
      toastStyle: {},
      rings: [],
      maskRects: [],   // blockers over admin-masked elements (no-click during this step)
      _audio: null,
      _timer: null,
      _advanceClick: null,
      _targetEl: null,
      _onReflow: null,
      _menuTimer: null,    // re-measures an open dropdown menu while it animates
      _openMenuEl: null,   // the currently-open dropdown menu being spotlighted
      reflowTick: 0,       // bumped on scroll/resize so the click-guard re-measures
      // DB-backed tours ({ flow: {steps:[...]} }), fetched on mount. Preferred
      // over the localStorage cache so a tour an admin saved shows for everyone.
      remoteTours: null,
    };
  },
  computed: {
    current() {
      return this.steps[this.stepIndex] || { caption: "" };
    },
    // Caption with the live-selection name tokens ({machine name}/{assembly name})
    // filled from the customer's current dropdown picks. stepIndex is a reactive dep
    // so it refills whenever the step changes (by which point the pick is made).
    displayCaption() {
      void this.stepIndex;
      return this.fillNameTokens((this.current && this.current.caption) || "", true);
    },
    stepNum() { return this.stepIndex + 1; },
    isLast() { return this._visibleFrom(this.stepIndex + 1, 1) < 0; },   // last VISIBLE step (skips show-if-hidden tail)
    // Master/admin can PREVIEW a tour-only page (no video yet); customers can't —
    // mirrors the catalog's canTakeTour, so the nav hand-off gates the same way.
    isMasterUser() {
      var u = (this.$store && this.$store.getters && this.$store.getters.currentUser) || {};
      if (u.isAdmin === true || u.isMaster === true) return true;
      var r = (u.role || "").toString().toLowerCase();
      return r === "master" || r === "admin";
    },
    hasClickable() { return !!this._targetEl || !!(this.current && this.current.highlights && this.current.highlights.length); },
    // Click-guard "mat": in GUIDED mode, block the WHOLE page EXCEPT the
    // spotlight hole (which already grows to wrap the control + its open menu /
    // results / diagram) and the tour card (higher z) — so the customer can only
    // do the highlighted action or exit via ✕, never click away/exit early.
    // Four strips tile the viewport around the hole. reflowTick + spotStyle keep
    // it reactive to scroll/resize.
    guardRects() {
      void this.reflowTick;
      if (!this.active || this.mode !== "guided") return [];
      var vw = window.innerWidth, vh = window.innerHeight;
      // Collect the HOLES the customer may click through: the primary spotlight
      // PLUS every extra highlight (e.g. "New Order", the second highlight) — so
      // the second highlight punches through the guard just like the first.
      var holes = [];
      var s = this.spotStyle || {};
      var W = parseFloat(s.width), H = parseFloat(s.height);
      // Round each hole OUT to integer edges: the guard strips share these edges,
      // so integer alignment keeps the (now-dimmed) strips seam-free, and rounding
      // outward keeps the dim from biting into the highlight.
      if (s.opacity !== 0 && W > 1 && H > 1) {
        var sl = parseFloat(s.left), st = parseFloat(s.top);
        holes.push({ l: Math.floor(sl), t: Math.floor(st), r: Math.ceil(sl + W), b: Math.ceil(st + H) });
      }
      (this.rings || []).forEach(function (rg) {
        var l = parseFloat(rg.left), t = parseFloat(rg.top), w = parseFloat(rg.width), h = parseFloat(rg.height);
        if (w > 1 && h > 1) holes.push({ l: Math.floor(l), t: Math.floor(t), r: Math.ceil(l + w), b: Math.ceil(t + h) });
      });
      // Top-nav buttons whose page has a live tour stay clickable mid-tour, so the
      // customer can hand off to another tour at any point — punch a hole for each.
      this._handoffEls().forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.width > 1 && r.height > 1) holes.push({ l: Math.floor(r.left), t: Math.floor(r.top), r: Math.ceil(r.right), b: Math.ceil(r.bottom) });
      });
      // No usable hole (narration-only / centered) → block everything; the
      // customer proceeds with the card (Next / ✕).
      if (!holes.length) return [{ left: "0px", top: "0px", width: vw + "px", height: vh + "px" }];
      // Subtract every hole from the viewport → guard rects covering all the rest.
      // (For a single hole this reduces to the old 4 strips.)
      var free = [{ l: 0, t: 0, r: vw, b: vh }];
      holes.forEach(function (hole) {
        var next = [];
        free.forEach(function (f) {
          if (hole.r <= f.l || hole.l >= f.r || hole.b <= f.t || hole.t >= f.b) { next.push(f); return; }   // no overlap
          var hl = Math.max(f.l, hole.l), hr = Math.min(f.r, hole.r), ht = Math.max(f.t, hole.t), hb = Math.min(f.b, hole.b);
          if (f.t < ht) next.push({ l: f.l, t: f.t, r: f.r, b: ht });   // band above the hole
          if (hb < f.b) next.push({ l: f.l, t: hb, r: f.r, b: f.b });   // band below
          if (f.l < hl) next.push({ l: f.l, t: ht, r: hl, b: hb });     // strip left
          if (hr < f.r) next.push({ l: hr, t: ht, r: f.r, b: hb });     // strip right
        });
        free = next;
      });
      return free.map(function (f) {
        return { left: f.l + "px", top: f.t + "px", width: Math.max(0, f.r - f.l) + "px", height: Math.max(0, f.b - f.t) + "px" };
      });
    },
  },
  mounted() {
    this._start = this.start.bind(this);
    this.$root.$on("start-guide-tour", this._start);
    var self = this;
    // Load the saved tours, THEN resume any guided tour a navigating click
    // carried to this page (the steps must be loaded before we can resume).
    this.loadRemoteTours().then(function () { self._maybeResume(); self._loadVideoFlows(); });
    this._refresh = this.loadRemoteTours.bind(this);
    this.$root.$on("guide-tours-updated", this._refresh);
  },
  beforeDestroy() {
    this.$root.$off("start-guide-tour", this._start);
    this.$root.$off("guide-tours-updated", this._refresh);
    this.teardown();
  },
  methods: {
    loadRemoteTours() {
      var self = this;
      return feapi.getGuideTours().then(function (d) { self.remoteTours = d || {}; }).catch(function () {});
    },
    // Resume a guided tour that a navigating click carried to a new page (within
    // a 5-min window, so a stale/abandoned tour can't surface later).
    _maybeResume() {
      // A nav click during a tour to a page WITHOUT its own tour leaves a brief
      // heads-up to surface here on arrival ("No guided tutorial for this page yet").
      try {
        var raw = localStorage.getItem("jcs_tour_navnote");
        if (raw) {
          localStorage.removeItem("jcs_tour_navnote");
          var note; try { note = JSON.parse(raw); } catch (e) { note = { msg: raw }; }   // back-compat with old string notes
          this._flashNavNote(note);
        }
      } catch (e) {}
      if (this.active) return;
      try {
        var act = JSON.parse(localStorage.getItem("jcs_tour_active") || "null");
        if (!act || !act.flow) return;
        if (act.t && (Date.now() - act.t) > 300000) { this.clearActive(); return; }
        this.start({ flow: act.flow, resumeStep: act.step || 0, preview: !!act.preview, recording: !!act.recording });
      } catch (e) {}
    },
    start(arg) {
      // arg is either a flow key string (load the saved tour) or an object from
      // the builder's Preview — { flow, steps } — whose steps are played exactly
      // as drafted (so preview reflects unsaved edits and mints no version).
      var flowKey, inlineSteps = null, resumeStep = 0;
      if (arg && typeof arg === "object") { flowKey = arg.flow; inlineSteps = arg.steps || null; resumeStep = arg.resumeStep || 0; }
      else { flowKey = arg; }
      this._isPreview = !!(arg && typeof arg === "object" && arg.preview);
      this.recording = !!(arg && typeof arg === "object" && arg.recording);   // builder sets this when screen-recording

      var rec = null;
      if (inlineSteps && inlineSteps.length) {
        rec = { steps: inlineSteps };
      } else if (this._isPreview) {
        // Resuming a PREVIEW after a navigating click → reload the admin's DRAFT
        // from localStorage (the in-memory inline steps were lost on the reload).
        try { rec = JSON.parse(localStorage.getItem("jcs_tour_rec_" + flowKey) || "null"); } catch (e) {}
      } else {
        // Prefer the DB copy (shared/persistent); fall back to the local cache.
        rec = (this.remoteTours && this.remoteTours[flowKey]) || null;
        if (!rec || !rec.steps || !rec.steps.length) {
          try { rec = JSON.parse(localStorage.getItem("jcs_tour_rec_" + flowKey) || "null"); } catch (e) {}
        }
      }
      if (rec && rec.steps && rec.steps.length) {
        // Built/recorded tour → GUIDED playback (customer drives).
        this.mode = "guided";
        this.steps = rec.steps.map(function (s, i) {
          var hl = (s.highlights || []).map(function (h) {
            return typeof h === "string" ? { selector: h, box: null, hlStyle: null } : { selector: h.selector, box: h.box || null, hlStyle: h.hlStyle || null };
          });
          // {showif:hasDrawing|noDrawing} — a leading marker that conditionally SHOWS
          // this step; the player skips it when the page state doesn't match. Strip it
          // from the spoken/displayed text and keep it as step.showIf for the skip logic.
          var txt = String(s.text || "");
          var showIf = null;
          var sm = txt.match(/\{showif:\s*([a-z]+)\s*\}/i);
          if (sm) { showIf = sm[1].toLowerCase(); txt = txt.replace(sm[0], "").replace(/\s{2,}/g, " ").trim(); }
          return {
            caption: txt
              ? txt.replace(/\{wait\}/gi, "").replace(/</g, "&lt;")
              : (s.label ? "<b>" + String(s.label).replace(/</g, "&lt;") + "</b>" : "Step " + (i + 1)),
            speak: txt,                     // typed narration → neural speech (RAW, keeps {wait} so playback can split)
            wait: /\{wait\}/i.test(txt),    // narration pauses at {wait} for a customer action
            showIf: showIf,                 // null | 'hasdrawing' | 'nodrawing' — conditional visibility
            // Audio source: a fresh PREVIEW clip (blob:/data: from the builder) plays
            // as-is; a STORED clip (/audio/{id}) is rewritten to the STABLE (flow,
            // position) url, which serves the active version's clip regardless of
            // version churn/pruning — so a stale cached step id can never 404.
            audioSrc: !s.audio ? null
              : (/^(blob:|data:)/.test(String(s.audio)) ? s.audio : feapi.clipUrl(flowKey, i)),
            target: s.target || null,
            type: s.type || s.stepType || null,   // 'dropdown' → open + select before advancing
            showNext: !!s.showNext,          // admin opted this step into a Next button
            resultMsg: s.resultMsg || "",    // input step: callout shown after they filter ({part} → matched code)
            targetBox: s.targetBox || null,  // admin-adjusted spotlight box
            highlights: hl,
            masks: (s.masks || []).map(function (m) { return typeof m === "string" ? { selector: m } : { selector: m.selector }; }),
            action: "guided",
          };
        });
      } else {
        if (!FLOWS[flowKey]) return;
        this.mode = "auto";
        this.steps = FLOWS[flowKey].map(function (s) {
          return {
            caption: s.caption,
            audioSrc: s.audio ? TOUR_BASE + flowKey + "/" + s.audio + ".mp3" : null,
            target: s.target || null,
            targetAlt: s.targetAlt || null,
            highlights: [],
            action: s.action || null,
            toast: s.toast || null,
          };
        });
      }
      this.flowKey = flowKey;
      // Resuming past the end means the tour already finished — clear + don't show.
      if (resumeStep >= this.steps.length) { this.clearActive(); return; }
      var firstVisible = this._visibleFrom(resumeStep > 0 ? resumeStep : 0, 1);
      if (firstVisible < 0) { this.clearActive(); return; }   // no step matches the current page state
      this.stepIndex = firstVisible;
      this.paused = false;
      this.active = true;
      this._onReflow = this.reposition.bind(this);
      window.addEventListener("scroll", this._onReflow, true);
      window.addEventListener("resize", this._onReflow);
      var selfKey = this;
      this._onKey = function (e) { if (e.key === "Escape" || e.keyCode === 27) selfKey.exit(); };
      window.addEventListener("keydown", this._onKey, true);
      // Recorded Preview Tour: follow the REAL cursor with a red ring (it's captured
      // in the screen recording). Move it directly on mousemove — no Vue re-render.
      this.recClicking = false;
      if (this.recording) {
        var selfRC = this;
        this.$nextTick(function () { var el = selfRC.$refs.recCursor; if (el) { el.style.left = "-100px"; el.style.top = "-100px"; } });
        this._onRecMove = function (e) { var el = selfRC.$refs.recCursor; if (el) { el.style.left = e.clientX + "px"; el.style.top = e.clientY + "px"; } };
        this._onRecDown = function () { selfRC.recClicking = true; };
        this._onRecUp = function () { selfRC.recClicking = false; };
        window.addEventListener("mousemove", this._onRecMove, true);
        window.addEventListener("mousedown", this._onRecDown, true);
        window.addEventListener("mouseup", this._onRecUp, true);
      }
      this._neuralDead = false;            // re-try neural each fresh tour run
      this._ttsVoice = "";                 // re-try the SAVED voice each run (fallback below pins Aria only after it proves unavailable)
      this.ensureAudioCtx();               // resume the audio ctx on THIS start click (gesture) so neural can play
      try { document.body.classList.add("gtour-active"); } catch (e) {}   // CSS raises the page's Add-to-Cart modal above the tour overlays so it's usable mid-tour
      // Whole-tour listener (capture): clicking a top-nav link whose page has its own
      // live tour hands off to that tour. Lives for the whole run (≠ the per-step
      // _advanceClick), removed in teardown().
      this._handoffClickBound = this._handoffClick.bind(this);
      try { document.addEventListener("click", this._handoffClickBound, true); } catch (e) {}
      // While RECORDING: keep the in-tab recorder alive by SPA-converting EVERY
      // same-origin link click (not just step-advance ones, so narration steps and the
      // nav holes can't full-reload). And on beforeunload, stamp the current step/URL
      // → if a hard reload still slips through (e.g. a programmatic redirect), the
      // builder can tell the admin exactly which step broke it.
      if (this.recording) {
        this._recNavGuard = this._spaNavIfLink.bind(this);
        try { document.addEventListener("click", this._recNavGuard, true); } catch (e) {}
        var selfRec = this;
        this._recUnloadGuard = function () {
          try { localStorage.setItem("jcs_tour_rec_break", JSON.stringify({ step: selfRec.stepIndex + 1, total: selfRec.steps.length, url: location.pathname + location.search })); } catch (e) {}
        };
        try { window.addEventListener("beforeunload", this._recUnloadGuard); } catch (e) {}
      }
      this.$nextTick(this.runStep);
    },

    async runStep() {
      this.clearTimer();
      this.stopAudio();
      this.stopSpeak();
      this.clearAdvanceClick();
      this.clearMenuTimer();
      this.clearGrowTimer();
      this._openMenuEl = null;
      this.toast = "";
      this.resultCallout = "";
      this.clicking = false;
      this.awaitingClick = false;
      this.rings = [];
      this.maskRects = [];
      this._targetEl = null;
      this._modalStep = false;
      var step = this.current;
      this._recordStepUrl();   // remember this step's page so ‹ Back can browser-back across pages

      // primary element = target (fallback: first marked highlight)
      var el = await this.resolveTarget(step.target, 1600);
      if (!el && step.targetAlt) el = await this.resolveTarget(step.targetAlt, 600);
      if (!el && step.highlights && step.highlights.length) el = await this.resolveTarget(step.highlights[0], 600);

      if (el) {
        this.scrollIntoView(el);
        await this.delay(260);
        this._targetEl = step.target ? el : null;
        this.positionAt(el);
      } else {
        this.positionCenter();
      }
      this.drawRings(step.highlights);
      this.drawMasks(step.masks);
      this.startMaskTracking();   // keep every masked element covered as the list reflows (filter/images/expand)

      if (this.mode === "auto") {
        // self-driving demo: perform the action, simulate writes, auto-advance
        if (el && step.action === "click") await this.animateClick(el, true);
        else if (step.action === "simulate") { await this.animateClick(el, false); if (step.toast) this.showToast(el); }
        else if (el) await this.animateClick(el, false);
        this.narrate(step, true);
      } else {
        // If the Add-to-Cart modal is open, THIS is the "complete the modal" step
        // (the prior step's cart click opened it + advanced here): wait for the
        // customer to enter the PO# + quantity and click Add to Cart — don't
        // auto-advance after the narration even with no on-page target — and dock
        // the narration card BESIDE the raised modal so the step text stays visible.
        var modalStep = this.isCartModalOpen();
        this._modalStep = modalStep;
        if (modalStep) this.placeCardBesideModal();
        // guided: play the voice, then wait for the customer to click.
        this.narrate(step, false);
        // Listen for the advancing click when there's a primary target, an open
        // modal, OR extra highlights (e.g. a {wait} step whose advance is the
        // second highlight like "New Order").
        if (this._targetEl || modalStep || (step.highlights && step.highlights.length)) {
          this.awaitingClick = true;
          this.waitForUserClick();
        }
      }
    },

    narrate(step, autoAdvanceWhenDone) {
      var self = this;
      this.clearTimer();
      this.stopSpeak();
      this._clearWaitListener();
      this.waitHint = false;
      // {wait} steps: speak each segment, PAUSE at the token until the customer
      // does the mapped action (a change in the primary highlight) or clicks Next,
      // then speak the rest. A single pre-rendered clip can't pause mid-way, so
      // these always synthesize per segment via neural.
      if (step.wait && step.speak && /\{wait\}/i.test(step.speak)) {
        this._speakWithWaits(step, autoAdvanceWhenDone);
        return;
      }
      // {machine name}/{assembly name}: fill from the live selection, and force FRESH
      // synthesis (a pre-recorded clip can't contain the customer's pick).
      var spokenText = this.fillNameTokens(step.speak);
      var hasNameTok = this._hasNameToken(step.speak);
      var go = function () {
        if (autoAdvanceWhenDone && !self.paused) self.autoNext();
        else if (autoAdvanceWhenDone === false && !self._targetEl && !self._modalStep && !(self.current && self.current.showNext)) self.autoNext(); // guided narration-only step (NOT while the Add-to-Cart modal is the focus, NOT when the admin set an explicit Next)
      };
      var timerFallback = function () {
        self.clearTimer();
        if ((autoAdvanceWhenDone || !self._targetEl) && !self._modalStep) self._timer = setTimeout(go, estimateMs(step.caption));
      };
      // NO browser-voice fallback. The tour speaks in the chosen Azure NEURAL
      // voice (e.g. Andrew) or stays SILENT — NEVER the flat browser
      // SpeechSynthesis voice. If a clip can't be fetched/synthesized, we just
      // advance silently on a timer. (Per user: "I want Andrew every time, never
      // the browser's voice.")
      // 1) Recorded clip OR pre-synthesized neural mp3 → play through WEB AUDIO.
      if (step.audioSrc && !hasNameTok) {
        // If the stored clip can't be fetched/decoded (missing or expired file on the
        // server → a tiny non-audio error body), DON'T go silent: fall back to FRESH
        // neural synthesis of the narration text before the silent timer. This keeps
        // the tour audible even when the saved clips are gone (re-saving the tour
        // regenerates them).
        var clipFail = function () {
          if (step.speak) self.neuralSpeak(spokenText, go, timerFallback);
          else timerFallback();
        };
        this.playClipUrl(step.audioSrc, go, clipFail);
        return;
      }
      // 2) Typed narration → Azure NEURAL voice via Web Audio; silent timer on fail.
      //    Also the path for {machine name}/{assembly name} steps (fresh synth so the
      //    voice speaks the customer's live pick, not a stale recorded value).
      if (step.speak) {
        this.neuralSpeak(spokenText, go, timerFallback);
        return;
      }
      // 3) Nothing to say → timer.
      timerFallback();
    },

    // Speak a narration that has {wait} pause point(s): split into segments and
    // speak them one at a time, pausing between for the mapped customer action.
    _speakWithWaits(step, autoAdvanceWhenDone) {
      var self = this;
      var segs = String(this.fillNameTokens(step.speak)).split(/\{wait\}/i).map(function (s) { return s.trim(); });
      var i = 0;
      var done = function () {
        // All segments spoken. Honor the normal end behavior (auto-advance only
        // for narration-only steps with no target — {wait} steps normally have an
        // extra highlight to click, so they wait).
        if (autoAdvanceWhenDone && !self.paused) self.autoNext();
        else if (autoAdvanceWhenDone === false && !self._targetEl && !self._modalStep &&
                 !(self.current && self.current.highlights && self.current.highlights.length) &&
                 !(self.current && self.current.showNext)) self.autoNext();
      };
      var speakNext = function () {
        if (!self.active) return;
        if (i >= segs.length) { done(); return; }
        var seg = segs[i]; i++;
        var afterSeg = function () {
          if (!self.active) return;
          if (i < segs.length) self._enterWait(speakNext);   // more text after this {wait}
          else done();
        };
        if (!seg) { afterSeg(); return; }                    // empty segment (e.g. leading {wait})
        self.neuralSpeak(seg, afterSeg, afterSeg);           // neural; resume on end OR fail
      };
      speakNext();
    },
    // Pause narration until the customer changes the primary highlight (any input/
    // change/commit inside it) or clicks Next/Skip. Then `resume` speaks the rest.
    _enterWait(resume) {
      var self = this;
      this._clearWaitListener();
      this._waitResume = resume;
      this.waitHint = true;            // card shows "make the change, or Skip"
      var field = this._targetEl;      // the FIRST/primary highlight = the {wait} field
      this._waitListener = function (e) {
        var t = e.target; if (!t || !t.closest) return;
        var commit = t.closest(".insights-qty-save, .insights-qty-input");   // the cart's qty edit/commit
        var inField = field && field.contains && field.contains(t);
        if (commit || (inField && (e.type === "input" || e.type === "change"))) self._resolveWait();
      };
      document.addEventListener("input", this._waitListener, true);
      document.addEventListener("change", this._waitListener, true);
      document.addEventListener("click", this._waitListener, true);
    },
    _resolveWait() {
      if (!this._waitResume) return;
      var r = this._waitResume; this._waitResume = null;
      this.waitHint = false;
      this._clearWaitListener();
      r();
    },
    _clearWaitListener() {
      if (this._waitListener) {
        document.removeEventListener("input", this._waitListener, true);
        document.removeEventListener("change", this._waitListener, true);
        document.removeEventListener("click", this._waitListener, true);
        this._waitListener = null;
      }
    },

    // Recording-only: convert a same-app <a href> click into an SPA router push so
    // the page does NOT full-reload (which would kill the in-tab recorder). Skips
    // new-tab, external, hash, and non-http links — those don't reload this tab (or
    // can't be kept alive). Wired globally via _recNavGuard (capture, every click)
    // while recording, and also called per-step from _advanceClick; both are safe to
    // run together (the path===here guard + router.push .catch dedupe a double call).
    _spaNavIfLink(e) {
      var a = e.target && e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      if (a.target && a.target.toLowerCase() === "_blank") return;   // new tab → this tab + recorder unaffected
      var raw = a.getAttribute("href") || "";
      if (!raw || raw.charAt(0) === "#") return;                      // in-page anchor / no-op
      if (/^(mailto:|tel:|javascript:|data:|blob:)/i.test(raw)) return;
      var path = raw;
      if (/^[a-z]+:\/\//i.test(raw) || raw.indexOf("//") === 0) {     // absolute URL
        try {
          var u = new URL(raw, window.location.href);
          if (u.origin !== window.location.origin) return;            // external → can't keep the capture; let it go
          path = u.pathname + u.search + u.hash;
        } catch (err) { return; }
      }
      if (path.charAt(0) !== "/") return;                             // only clean app paths; anything else navigates natively (no regression)
      try { e.preventDefault(); } catch (err) {}                      // cancel the full-reload navigation
      var here = (this.$route && this.$route.fullPath) || (window.location.pathname + window.location.search);
      if (path === here) return;                                      // already here → no nav (the step still advances)
      try { this.$router.push(path).catch(function () {}); }
      catch (err) { try { window.location.assign(path); } catch (e2) {} }
    },
    waitForUserClick() {
      var self = this;
      var dropdown = this.isDropdownStep();
      var diagram = this.isDiagramStep();
      var input = !dropdown && !diagram && this.isInputStep();
      // For a dropdown the interaction AREA is the whole container: the step
      // usually targets a sub-element (the ".default text" placeholder), while
      // the option rows AND the hidden <input> that fires 'change' are SIBLINGS
      // of it — a pick there must still count, so scope to the container.
      var area = this._targetEl;
      if (dropdown && area) {
        try { var dd = area.closest && area.closest('.ui.dropdown,.dropdown,[role="combobox"]'); if (dd) area = dd; } catch (e) {}
      }
      this._advanceArea = area;
      var inArea = function (node) { return !!(area && (node === area || (area.contains && area.contains(node)))); };
      // EXTRA highlights are first-class click targets too: a click on any of them
      // (e.g. "New Order", the second highlight) advances the step — same as the
      // primary spotlight. Resolved fresh so list re-renders don't strand them.
      var extraEls = [];
      (this.current.highlights || []).forEach(function (h) { var el = self.looseQuery(h && h.selector); if (el) extraEls.push(el); });
      var inExtra = function (node) { return extraEls.some(function (el) { return node === el || (el.contains && el.contains(node)); }); };
      var advanced = false;
      // When the admin enabled an explicit Next for this step (showNext), ONLY the
      // Next button advances it — clicks, dropdown/diagram picks, change events, and
      // the cart-open auto-advance must NOT. The click still reaches the page (so the
      // customer can interact with the highlighted control), it just doesn't move the
      // tour forward. Guarding advance() in one place covers every advance path below.
      var nextOnly = !!(self.current && self.current.showNext);
      var advance = function () {
        if (nextOnly || advanced) return; advanced = true;
        self.clearAdvanceClick();
        // Persist progress NOW so the tour survives if this click NAVIGATES to a
        // new page (e.g. clicking 'New Order' reloads) — the next page resumes
        // at this step. If it doesn't navigate, the timer advances in place.
        self.persistActive(self.stepIndex + 1);
        setTimeout(function () { if (self.active) self.next(); }, 650);
      };
      // "Click the cart" step: its target IS (or contains) a cart-add button. The
      // customer may click ANY row's cart, and the step's goal is simply that the
      // Add-to-Cart modal OPENS — so advance as soon as it appears, not only on a
      // click of the exact highlighted instance. (Clicking a DIFFERENT row's cart
      // opened the modal but left inArea false → the tour got stuck on step 7.)
      // The next step starts with the modal already open → it's the "fill it in" step.
      var cartStep = !!(area && (
        (area.closest && area.closest(".inline-wear-part-add")) ||
        (area.querySelector && area.querySelector(".inline-wear-part-add"))
      ));
      if (cartStep && !this.isCartModalOpen()) {
        this._cartOpenWatch = setInterval(function () {
          if (!self.active) { clearInterval(self._cartOpenWatch); self._cartOpenWatch = null; return; }
          if (self.isCartModalOpen()) { clearInterval(self._cartOpenWatch); self._cartOpenWatch = null; advance(); }
        }, 150);
      }
      this._advanceClick = function (e) {
        // During a RECORDED preview, keep THIS document (and the in-tab screen
        // recorder) alive across page changes by turning a same-app link click into
        // an SPA router navigation instead of a full reload. A full reload destroys
        // the MediaRecorder + everything captured so far (hard browser limit), which
        // is why a multi-page recording "couldn't save". Customer tours (recording
        // = false) are untouched — they still full-reload + resume via jcs_tour_active.
        if (self.recording) self._spaNavIfLink(e);
        // ZOOM controls (+/−/Reset on the parts drawing — inline locked or modal)
        // are exploration, NOT progress. They must NEVER advance the tour, on any
        // step. Checked FIRST so it wins even if the step isn't detected as a
        // diagram step. (Only action items — a real pick — advance.)
        if (e.target.closest && e.target.closest('.locked-zoom-btn, .custom-image-controls, [title="Zoom in"], [title="Zoom out"]')) return;
        // A balloon/callout selection on the parts diagram is always a real
        // "pick" → advance, for ANY step. (Its @click.stop runs in the bubble
        // phase, AFTER this capture-phase listener, so we still see it.)
        if (e.target.closest && e.target.closest(".callout-hotspot")) { advance(); return; }
        // The page's Add-to-Cart MODAL is part of the walkthrough. It's raised
        // above the tour overlays by body.gtour-active (CSS) so it's fully usable.
        // A click INSIDE it must NOT advance the step (the customer is entering the
        // PO# + quantity) — EXCEPT the primary confirm (footer "Add to Cart" / OK),
        // which completes the step. The cart ICON itself is NOT handled here: it
        // falls through to the normal advance path, so a "click the cart" step both
        // ADVANCES (to the next "fill it in" step) and opens the modal for it.
        if (e.target.closest && e.target.closest("#addToCartModal, .add-to-cart-modal")) {
          if (e.target.closest(".modal-footer .btn-primary:not(:disabled):not(.disabled)")) advance();
          return;
        }
        // On a "click the cart" step, a click on ANY cart-add button (not just the
        // highlighted one) opens the modal and completes the step.
        if (cartStep && e.target.closest && e.target.closest(".inline-wear-part-add")) { advance(); return; }
        // The cart's green "save quantity" check ("adjust the quantity" step): show
        // the step's result callout (e.g. "New quantity saved!") when the customer
        // COMMITS the edit — not while typing. The click still reaches the cart to
        // commit the qty; we DON'T advance (the customer proceeds via Next).
        if (e.target.closest && e.target.closest(".insights-qty-save")) {
          var rmsg = self.current && self.current.resultMsg;
          if (rmsg) self.resultCallout = self.fillResultMsg(rmsg, "", 0);
          return;
        }
        // Clicking an EXTRA highlight (e.g. "New Order", the second highlight)
        // advances — extra highlights are first-class click targets.
        if (inExtra(e.target)) { advance(); return; }
        // On a {wait} step the PRIMARY highlight is the wait FIELD (the customer
        // edits it to resume the voice; the wait listener handles that) — clicking
        // it must NOT advance. Advance comes only from an extra highlight (above)
        // or Next/Skip.
        if (self.current && self.current.wait) return;
        // A diagram/select step must NOT advance on anything else — zooming
        // (+/−), dragging the drawing, or clicking empty image all stay put so
        // the customer can explore and only a real balloon pick (above) advances.
        // Only the ✕ / Esc exits.
        if (diagram) return;
        // Input/search step: the customer TYPES here — focusing/clicking the
        // field (or its results) must not advance. They proceed via Next (which
        // is shown for input steps). Typing extends the spotlight (listener below).
        if (input) return;
        if (!inArea(e.target)) return;
        if (dropdown) {
          // Dropdown step has TWO highlight states:
          //  1) closed control  → 2) the OPEN menu (so the customer sees the
          //     whole list to pick from). The first click just OPENS it, so we
          //     grow the spotlight to wrap the menu instead of advancing.
          //     We only advance when the customer actually PICKS a value — a
          //     click on an option (.item / <option> / [role=option]), or the
          //     'change' handler below (covers native <select> + Semantic's
          //     hidden input). Clicking the control again just re-opens it.
          var onItem = !!(e.target.closest && e.target.closest('.item,[role="option"],option,li'));
          var noSelect = !!(e.target.closest && e.target.closest('[data-no-select]'));
          if (onItem && !noSelect) { advance(); return; }
          self.spotlightOpenMenu();
          return;
        }
        advance();
      };
      document.addEventListener("click", this._advanceClick, true);
      // The selection 'change' fires on the hidden input / native <select> inside
      // the container (a sibling of the targeted label, so it wouldn't bubble
      // through it) — listen on DOCUMENT and gate by the container instead.
      if (dropdown) {
        this._advanceChange = function (e) { if (inArea(e.target)) advance(); };
        document.addEventListener("change", this._advanceChange, true);
      }
      // Input/search step: when the customer starts typing, extend the spotlight
      // to also cover the results that derive from the search.
      if (input) {
        this._onTypeInput = function (e) {
          // Grow the spotlight to the results + fire the callout ONLY for typing in
          // THIS step's own search field: the Search-page parts FILTER, OR the
          // step's target (e.g. the header search box, whose matches drop down like
          // a menu). A document-level listener would otherwise fire for every input
          // on the page; and a NON-search input (e.g. the cart QUANTITY on the
          // "adjust quantity" step) must NOT drag the spotlight onto results or fire
          // the callout on a keystroke — that's enforced downstream too, since
          // inputResultsEl() returns null for a field with no results/suggestions.
          var t = e.target;
          var inField = t && (
            (t.closest && t.closest(".part-filter-input")) ||
            (self._targetEl && (t === self._targetEl || (self._targetEl.contains && self._targetEl.contains(t))))
          );
          if (!inField) return;
          self.spotlightInputResults();
          self.updateResultCallout(e);
        };
        document.addEventListener("input", this._onTypeInput, true);
      }
      // Cart step (admin marked it "Cart (auto-expand)"): the staging cart GROWS as the
      // customer adds parts/quotes (the "Your Quotes In Progress" section appears).
      // Keep the spotlight wrapped around the WHOLE tray and follow that growth live,
      // the same way a dropdown's spotlight grows to its open menu — so newly-added
      // sections are never cut off the highlight. Opt-in (not every cart-targeting step
      // wants the whole tray — e.g. a precise "click Review & Place Order" button step).
      if (this.isCartGrowStep() && this.cartGrowEl()) {
        this.spotlightCartGrows();
      }
    },
    // Input step: after the customer types + narrows the results, show the
    // admin's result message with {part} → the top matching part code (read from
    // the first result row's data-part-code). Cleared when the filter is empty.
    updateResultCallout(e) {
      var self = this;
      var msg = this.current && this.current.resultMsg;
      var typed = (e && e.target && typeof e.target.value === "string") ? e.target.value.trim() : "x";
      if (this._resultTimer) { clearTimeout(this._resultTimer); this._resultTimer = null; }
      if (!msg || !typed) { this.resultCallout = ""; return; }
      // DEBOUNCE + defer: the results live in a DIFFERENT component and re-render on
      // their next tick, so reading the DOM synchronously here gets the PREVIOUS
      // keystroke's rows (the "got DSD S 0416 instead of 0417" bug). Wait for the
      // list to settle, then read the top match from whatever results THIS field
      // produced — the parts list ('.inline-wear-part[data-part-code]') OR an
      // autocomplete dropdown ('.dropdown-option'/'.item' with an '.option-code'
      // span). A field with no results (e.g. the cart quantity) clears the callout.
      this._resultTimer = setTimeout(function () {
        var resEl = self.inputResultsEl();
        if (!resEl) { self.resultCallout = ""; return; }
        var code = "", count = 0;
        var rows = resEl.querySelectorAll(".inline-wear-part");
        if (rows && rows.length) {
          code = rows[0].getAttribute("data-part-code") || "";
          count = rows.length;
        } else {
          var opts = resEl.querySelectorAll('.dropdown-option, .item, [role="option"], li');
          if (opts && opts.length) {
            var codeEl = opts[0].querySelector(".option-code");
            code = ((codeEl ? codeEl.textContent : opts[0].textContent) || "").trim();
            count = opts.length;
          }
        }
        self.resultCallout = self.fillResultMsg((self.current && self.current.resultMsg) || msg, code, count);
        self.spotlightInputResults();   // re-measure the union now the list settled
      }, 180);
    },
    fillResultMsg(msg, code, count) {
      var safe = String(code || "").replace(/</g, "&lt;");
      return String(msg)
        .replace(/</g, "&lt;")
        .replace(/\{part(?:\s*code)?\}|%part ?code%|%part%/gi, "<b>" + safe + "</b>")
        .replace(/\{count\}/gi, String(count || ""));
    },
    // ===== Live-selection name tokens =====
    // {machine name} / {assembly name} let the narration read back the customer's
    // current dropdown picks. The values are dynamic (depend on what THEY selected),
    // so a step using them always synthesizes FRESH (a stored clip can't contain them).
    _hasNameToken(text) {
      return /\{\s*(machine|assembly)(?:\s*name)?\s*\}|\{\s*part(?:\s+(name|price|code))?\s*\}/i.test(String(text || ""));
    },
    // The part this step is about, for {part name}/{part price}. Priority: the part
    // row THIS step highlights (the author pointed at it) → a header-search-highlighted
    // part → the first visible part row. Values come from data-part-* on the row.
    _currentPart() {
      var row = null;
      var t = this._targetEl;
      if (t && t.closest) row = t.closest(".inline-wear-part");
      if (!row) row = document.querySelector(".inline-wear-part.is-header-highlight");
      if (!row) {
        var rows = document.querySelectorAll(".inline-wear-part");
        for (var i = 0; i < rows.length; i++) { if (this.isVisible(rows[i])) { row = rows[i]; break; } }
      }
      if (!row) return null;
      return {
        code: (row.getAttribute("data-part-code") || "").trim(),
        name: (row.getAttribute("data-part-name") || "").trim(),
        price: (row.getAttribute("data-part-price") || "").trim(),
      };
    },
    // Read the displayed text of a Semantic dropdown's current selection (its direct-
    // child .text node; the `default` class means nothing is picked yet).
    _selectedDropdownText(id) {
      var t = document.querySelector("#" + id + " > .text") || document.querySelector("#" + id + " .text");
      if (!t || (t.classList && t.classList.contains("default"))) return "";
      return (t.textContent || "").trim();
    },
    _machineName() {
      var t = this._selectedDropdownText("MachineSelect");
      if (!t) return "";
      // Per spec: don't read past the hyphen — speak the machine name only, dropping
      // the trailing " - CODE (id)" suffix (e.g. "COMBINATION: 40F - DDF (610216)"
      // → "COMBINATION: 40F"). Split on a SPACED hyphen so internal model hyphens
      // ("88C-RD", "36-24A") are preserved; if there's no " - ", use the whole text.
      return t.split(/\s+-\s+/)[0].trim();
    },
    _assemblyName() { return this._selectedDropdownText("WearAssemblySelect"); },
    // Replace the name tokens with the live picks. Tolerates spacing/case and the
    // shorthand {machine}/{assembly}. Falls back to a generic phrase if nothing's
    // picked yet so the sentence still reads.
    fillNameTokens(text, html) {
      if (!text || (text.indexOf("{") < 0)) return text;
      var self = this;
      // The CAPTION is rendered via v-html, so escape the inserted name there (catalog
      // names can contain &, e.g. "WASHER & CAPSCREW"). The SPOKEN text stays raw — the
      // backend TTS handles it.
      var esc = function (v) {
        return html ? String(v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") : v;
      };
      var part = null;
      var cp = function () { if (part === null) part = self._currentPart() || {}; return part; };   // resolve once, lazily
      return String(text)
        .replace(/\{\s*machine(?:\s*name)?\s*\}/gi, function () { return esc(self._machineName() || "your machine"); })
        .replace(/\{\s*assembly(?:\s*name)?\s*\}/gi, function () { return esc(self._assemblyName() || "your assembly"); })
        .replace(/\{\s*part\s+name\s*\}/gi, function () { return esc(cp().name || "this part"); })
        .replace(/\{\s*part\s+price\s*\}/gi, function () { return esc(cp().price || "the listed price"); })
        .replace(/\{\s*part\s+code\s*\}/gi, function () { return esc(cp().code || "this part"); })
        .replace(/\{\s*part\s*\}/gi, function () { return esc(cp().code || "this part"); });   // bare {part} = the part CODE (kept working in narration)
    },
    // Grow the spotlight to wrap the input field + the results list it drives,
    // so after the customer types they see both highlighted as one.
    spotlightInputResults() {
      var results = this.inputResultsEl();
      if (!results) return;
      this._inputResultsEl = results;
      this.paintUnionSpot(this._targetEl, results);
    },
    paintUnionSpot(a, b) {
      var rects = [];
      if (a && a.getBoundingClientRect) rects.push(a.getBoundingClientRect());
      if (b && b.getBoundingClientRect) rects.push(b.getBoundingClientRect());
      if (!rects.length) return;
      var left = 1e9, top = 1e9, right = -1e9, bottom = -1e9;
      rects.forEach(function (r) { left = Math.min(left, r.left); top = Math.min(top, r.top); right = Math.max(right, r.right); bottom = Math.max(bottom, r.bottom); });
      var pad = 6;
      var rr = { left: left - pad, top: top - pad, width: (right - left) + pad * 2, height: (bottom - top) + pad * 2 };
      this.spotStyle = { left: rr.left + "px", top: rr.top + "px", width: rr.width + "px", height: rr.height + "px", opacity: 1 };
      this.placeCard({ left: rr.left, top: rr.top, bottom: rr.top + rr.height, right: rr.left + rr.width });
    },
    // After a dropdown opens, grow the primary spotlight to wrap the whole open
    // menu (control + list) so the customer sees the entire set of choices
    // highlighted. The menu is measured live, so it fits however many items are
    // shown. No-op for a native <select> (its popup isn't in the DOM) — the
    // control stays highlighted and 'change' still advances on selection.
    spotlightOpenMenu() {
      var self = this;
      this.clearMenuTimer();
      // Poll while the step is awaiting a click: follow the open menu's live
      // rect, and — crucially — when it CLOSES (e.g. the customer clicks away
      // without picking) shrink the spotlight back to the control instead of
      // leaving a stale oversized box. Repaint only when the rect changes so the
      // CSS transition doesn't restart every tick. clearMenuTimer (on advance/
      // exit/new step) stops the poll.
      var lastKey = "";
      var tick = function () {
        if (!self.active || !self.awaitingClick) { self._menuTimer = null; return; }
        var menu = self.findOpenMenu();
        if (menu) {
          var r = menu.getBoundingClientRect();
          var key = Math.round(r.left) + "," + Math.round(r.top) + "," + Math.round(r.width) + "," + Math.round(r.height);
          if (key !== lastKey) { lastKey = key; self._openMenuEl = menu; self.paintMenuSpot(menu); }
        } else if (self._openMenuEl) {
          self._openMenuEl = null; lastKey = "";
          if (self._targetEl) self.positionAt(self._targetEl);   // menu closed → revert to the control
        }
        self._menuTimer = setTimeout(tick, 120);
      };
      tick();
    },
    findOpenMenu() {
      var el = this._targetEl;
      if (!el) return null;
      // The dropdown CONTAINER may be the target itself, an ancestor of it, or
      // its parent (the target could be a sub-element like the hidden input or
      // the "default text" label). Search all of them for the open list.
      var root = el;
      try { var dd = el.closest && el.closest('.ui.dropdown,.dropdown,[role="combobox"]'); if (dd) root = dd; } catch (e) {}
      this._ddRoot = root;
      var scopes = [];
      [el, root, root.parentElement].forEach(function (x) { if (x && scopes.indexOf(x) < 0) scopes.push(x); });
      var sels = [".menu.visible", ".menu.transition.visible", ".menu", ".dropdown-menu.show", ".dropdown-menu", '[role="listbox"]', "ul[role]"];
      for (var s = 0; s < scopes.length; s++) {
        for (var i = 0; i < sels.length; i++) {
          var m = null;
          try { m = scopes[s].querySelector(sels[i]); } catch (e) {}
          if (m && this.isVisible(m) && m.getBoundingClientRect().height > 8) return m;
        }
      }
      // some libraries portal the open list to <body> and link it via aria-owns
      try {
        var owns = (root.getAttribute && root.getAttribute("aria-owns")) || (el.getAttribute && el.getAttribute("aria-owns"));
        if (owns) { var o = document.getElementById(owns); if (o && this.isVisible(o)) return o; }
      } catch (e) {}
      return null;
    },
    paintMenuSpot(menu) {
      var M = menu.getBoundingClientRect();
      var rect = { left: M.left, top: M.top, right: M.right, bottom: M.bottom };
      var ctrl = this._ddRoot || this._targetEl;
      if (ctrl) {
        var C = ctrl.getBoundingClientRect();
        rect.left = Math.min(rect.left, C.left); rect.top = Math.min(rect.top, C.top);
        rect.right = Math.max(rect.right, C.right); rect.bottom = Math.max(rect.bottom, C.bottom);
      }
      var pad = 6;
      var r = { left: rect.left - pad, top: rect.top - pad, width: (rect.right - rect.left) + pad * 2, height: (rect.bottom - rect.top) + pad * 2 };
      this.spotStyle = { left: r.left + "px", top: r.top + "px", width: r.width + "px", height: r.height + "px", opacity: 1 };
      this.placeCard({ left: r.left, top: r.top, bottom: r.top + r.height, right: r.left + r.width });
    },
    clearMenuTimer() { if (this._menuTimer) { clearTimeout(this._menuTimer); this._menuTimer = null; } },
    // A step the admin marked "Cart (auto-expand)" — its spotlight should wrap the whole
    // staging cart and grow as items are added. Opt-in via type:'cart' so precise cart
    // steps (e.g. highlighting just the Review & Place Order button) aren't over-grown.
    isCartGrowStep() {
      var s = this.current;
      return !!(s && (s.type === "cart" || s.stepType === "cart"));
    },
    // The floating staging cart wrapper for this step, if the step targets anything
    // inside the cart. It contains BOTH the "Your Order In Progress" and "Your Quotes
    // In Progress" sections and grows as items are added — so spotlighting IT (not the
    // sub-element the admin happened to pick) wraps the whole cart. Handles a target
    // inside the tray, the cart host itself, or an ancestor of the host.
    cartGrowEl() {
      var el = this._targetEl;
      if (!el || !el.closest) return null;
      var w = el.closest(".staging-tray-wrapper");
      if (w) return w;
      var host = el.closest(".cart-widget-host");
      if (host && host.querySelector) { var inner = host.querySelector(".staging-tray-wrapper"); if (inner) return inner; }
      return null;
    },
    // Poll the cart tray's live rect while awaiting the click and keep the spotlight
    // wrapped around the WHOLE cart, so as the customer adds parts/quotes (the tray
    // grows, "Your Quotes In Progress" appears) the highlight expands with it instead
    // of cutting the new section off — mirrors spotlightOpenMenu for dropdowns. Repaint
    // only when the rect changes so the CSS transition doesn't restart every tick.
    spotlightCartGrows() {
      var self = this;
      this.clearGrowTimer();
      var lastKey = "";
      var tick = function () {
        if (!self.active || !self.awaitingClick) { self._growTimer = null; return; }
        var el = self.cartGrowEl();
        if (el) {
          var r = el.getBoundingClientRect();
          if (r.width > 2 && r.height > 2) {
            var key = Math.round(r.left) + "," + Math.round(r.top) + "," + Math.round(r.width) + "," + Math.round(r.height);
            if (key !== lastKey) { lastKey = key; self.paintRectSpot(r); }
          }
        }
        self._growTimer = setTimeout(tick, 150);
      };
      tick();
    },
    clearGrowTimer() { if (this._growTimer) { clearTimeout(this._growTimer); this._growTimer = null; } },
    // Paint the spotlight hole (and dock the card) to an arbitrary rect — used to wrap
    // the whole cart tray live without disturbing _targetEl / click-area detection.
    paintRectSpot(R) {
      var pad = 6;
      var r = { left: R.left - pad, top: R.top - pad, width: R.width + pad * 2, height: R.height + pad * 2 };
      this.spotStyle = { left: r.left + "px", top: r.top + "px", width: r.width + "px", height: r.height + "px", opacity: 1 };
      this.placeCard({ left: r.left, top: r.top, bottom: r.top + r.height, right: r.left + r.width });
    },
    // A step is "dropdown" if the admin marked it so, or its target IS a dropdown
    // (native <select>, Semantic .dropdown, or an ARIA combobox/listbox).
    isDropdownStep() {
      var s = this.current;
      if (s && (s.type === "dropdown" || s.stepType === "dropdown")) return true;
      var el = this._targetEl;
      if (!el) return false;
      try {
        var sel = 'select,.dropdown,.ui.dropdown,[role="combobox"],[role="listbox"]';
        if ((el.tagName || "").toLowerCase() === "select") return true;
        if (el.matches && el.matches(sel)) return true;
        // The step may target a SUB-element of the dropdown (e.g. the
        // ".default text" placeholder label, as the auto-picked selector often
        // does) — so look UPWARD for the dropdown container too, not just down.
        if (el.closest && el.closest(sel)) return true;
        if (el.querySelector && el.querySelector(sel)) return true;
      } catch (e) {}
      return false;
    },
    // A step is a "diagram/select" step when its target is the parts drawing AND
    // the page has selectable balloon hotspots (.callout-hotspot). Such a step
    // must NOT advance on zoom/drag/empty image clicks — only when the customer
    // actually clicks a balloon (a real part selection). Auto-detected: an <img>
    // (or a wrap containing one) while callout hotspots exist on the page.
    isDiagramStep() {
      var el = this._targetEl;
      if (!el) return false;
      try {
        if (!document.querySelector(".callout-hotspot")) return false;   // no selectable balloons → not a diagram step
        if ((el.tagName || "").toLowerCase() === "img") return true;     // target IS the drawing
        // OR the target is the drawing's stable WRAPPER panel (we now anchor
        // diagram highlights there for stability) — recognize it by the known
        // diagram-container classes, with a balloon hotspot inside.
        if (el.matches && el.matches(".machine-image-wrap, .diagram-zoom-wrap") && el.querySelector(".callout-hotspot")) return true;
        // OR the target is the drawing's immediate WRAP: a DIRECT-child <img>
        // plus a balloon hotspot inside. (Direct-child only, so a big form
        // wrapper that merely CONTAINS the diagram deep down isn't misclassified.)
        var kids = el.children || [], hasDirectImg = false;
        for (var i = 0; i < kids.length; i++) { if ((kids[i].tagName || "").toLowerCase() === "img") { hasDirectImg = true; break; } }
        if (hasDirectImg && el.querySelector && el.querySelector(".callout-hotspot")) return true;
      } catch (e) {}
      return false;
    },
    // A step is an "input/search" step when the admin marked it (type 'input')
    // or its target is a text field. The customer types into it — focusing/
    // typing must NOT advance the tour; they proceed via Next. After they type,
    // the spotlight extends to the results that derive from the search.
    isInputStep() {
      var s = this.current;
      if (s && (s.type === "input" || s.stepType === "input")) return true;
      var el = this._targetEl;
      if (!el) return false;
      try {
        var tag = (el.tagName || "").toLowerCase();
        if (tag === "textarea") return true;
        if (tag === "input") {
          var ty = (el.getAttribute("type") || "text").toLowerCase();
          return /^(text|search|number|email|tel|url|)$/.test(ty);   // a typeable field, not checkbox/radio/button
        }
        if (el.querySelector && el.querySelector('input[type="text"],input[type="search"],input:not([type]),textarea')) return true;
      } catch (e) {}
      return false;
    },
    // The element holding the results that derive from a search — used to extend
    // an input step's spotlight (and read the {part} top match). Two shapes:
    //  • an OPEN autocomplete/suggestions dropdown anchored to the step's search
    //    field (the header search's '.custom-dropdown', a Semantic '.menu.visible',
    //    a Bootstrap '.dropdown-menu.show', any ARIA listbox). This is what makes a
    //    free-text search "act like a dropdown": the spotlight wraps the open list
    //    so the card is pushed BELOW it instead of covering the suggestions;
    //  • the Search page's inline parts list that the '.part-filter-input' narrows.
    // Scoped so a non-search input (e.g. the cart quantity) returns null → the
    // spotlight/callout never fire for it.
    inputResultsEl() {
      var sug = this.findInputSuggestions();
      if (sug) return sug;
      var el = this._targetEl;
      var isFilterStep = el && (
        (el.matches && el.matches(".part-filter-input, .part-filter-wrap")) ||
        (el.closest && el.closest(".part-filter-wrap")) ||
        (el.querySelector && el.querySelector(".part-filter-input"))
      );
      if (isFilterStep) {
        try { var r = document.querySelector(".inline-wear-list"); return (r && this.isVisible(r)) ? r : null; } catch (e) {}
      }
      return null;
    },
    // Find an OPEN suggestions/autocomplete dropdown anchored to the step's search
    // field. Mirrors findOpenMenu (the machine/assembly dropdown logic) but adds the
    // header search's '.custom-dropdown', so a free-text search whose matches drop
    // down gets the same spotlight treatment as a real <select>. The list renders as
    // a sibling/descendant of the field's container, so scan the target, its input
    // container, and that container's parent. isVisible + a height floor skip a
    // closed (display:none) menu that still lives in the DOM.
    findInputSuggestions() {
      var el = this._targetEl;
      if (!el) return null;
      var root = el;
      try { var c = el.closest('.search-input-container,.ui.search,.ui.dropdown,.dropdown,[role="combobox"]'); if (c) root = c; } catch (e) {}
      var scopes = [];
      [el, root, root.parentElement].forEach(function (x) { if (x && scopes.indexOf(x) < 0) scopes.push(x); });
      var sels = [".custom-dropdown", ".menu.visible", ".menu.transition.visible", ".results.visible", ".dropdown-menu.show", ".dropdown-menu", '[role="listbox"]'];
      for (var s = 0; s < scopes.length; s++) {
        for (var i = 0; i < sels.length; i++) {
          var m = null;
          try { m = scopes[s].querySelector(sels[i]); } catch (e) {}
          if (m && this.isVisible(m) && m.getBoundingClientRect().height > 8) return m;
        }
      }
      return null;
    },
    // Top-nav links whose page has a live tour for a DIFFERENT flow than the one
    // running — these stay clickable (guardRects punches a hole) and hand off to it.
    // Top-nav links (other than the running tour's own) stay clickable mid-tour so the
    // customer can leave or jump tours. guardRects punches a hole over each.
    _handoffEls() {
      var self = this;
      var out = [];
      if (this.mode !== "guided") return out;
      try {
        var els = document.querySelectorAll("[data-tour-flow]");
        Array.prototype.forEach.call(els, function (el) {
          var flow = el.getAttribute("data-tour-flow");
          if (!flow || flow === self.flowKey) return;   // blank flow / this tour's own button → leave to the normal guard
          if (el.offsetParent === null) return;          // visible nav links only
          out.push(el);
        });
      } catch (e) {}
      return out;
    },
    // A click on one of those nav links: if that page has a live tour → hand off to it
    // (skipping its opening "click <nav>" step, since they're clicking it now). If it
    // has NO tour → end this tour and leave a brief heads-up to show on arrival. Either
    // way the nav navigates normally.
    _handoffClick(e) {
      if (!this.active || this.mode !== "guided") return;
      var navEl = e.target && e.target.closest && e.target.closest("[data-tour-flow]");
      if (!navEl) return;
      var flow = navEl.getAttribute("data-tour-flow");
      if (!flow || flow === this.flowKey) return;
      var t = this.remoteTours && this.remoteTours[flow];
      var hasTour = !!(t && t.steps && t.steps.length);
      var hasVideo = (this._videoFlows || []).indexOf(flow) >= 0;
      var label = (navEl.textContent || "").replace(/\s+/g, " ").trim();
      // Same rule as the catalog's canTakeTour: a Master can PREVIEW a tour-only page;
      // a customer needs a "ready" page (both a tour AND a video).
      if (hasTour && (this.isMasterUser || hasVideo)) {
        var resumeAt = t.steps.length > 1 ? 1 : 0;   // skip the opening nav-click step
        this.exit();   // close this tour (clears its persisted state)…
        // …then queue the TARGET; _maybeResume starts it on the destination page.
        try { localStorage.setItem("jcs_tour_active", JSON.stringify({ flow: flow, step: resumeAt, t: Date.now() })); } catch (x) {}
      } else {
        // Remember the tour we're leaving (flow, step, page) so the heads-up can offer
        // a one-click way back into it — captured BEFORE exit() clears the state.
        var back = { flow: this.flowKey, step: this.stepIndex, url: location.pathname + location.search, label: this._flowLabel(this.flowKey) };
        this.exit();   // not available to this user → end the current tour…
        // …and leave a heads-up (with the "back to the tutorial you left" action) that
        // surfaces on arrival; the nav still navigates.
        var msg = hasTour
          ? "“" + (label || "This page") + "” tutorial is coming soon."   // tour exists, no video → customer "coming soon"
          : "No guided tutorial for " + (label ? "“" + label + "”" : "this page") + " yet.";
        try { localStorage.setItem("jcs_tour_navnote", JSON.stringify({ msg: msg, back: back })); } catch (x) {}
      }
    },
    // Briefly show a standalone note (independent of a running tour), auto-dismissing.
    _flashNavNote(note) {
      var self = this;
      if (typeof note === "string") note = { msg: note };
      this.navNote = note;
      clearTimeout(this._navNoteT);
      // A note offering "back to the tutorial" stays until the user acts; a plain one auto-dismisses.
      if (!(note && note.back)) this._navNoteT = setTimeout(function () { self.navNote = ""; }, 4500);
    },
    // Friendly label for a tour flow (used in the "back to the … tutorial" note).
    _flowLabel(flow) {
      var m = { order: "New Order", quote: "New Quote", search: "Search" };
      return m[flow] || (flow ? (flow.charAt(0).toUpperCase() + flow.slice(1)) : "previous");
    },
    // The "↩ Back to the … tutorial" action: re-queue the tour the customer left and
    // navigate back to the page it was on (or resume in place if already there).
    resumeNavBack() {
      var b = this.navNote && this.navNote.back;
      this.navNote = "";
      if (!b || !b.flow) return;
      // Re-enter that tour at its first on-page step — the nav click that brought the
      // customer here cleared the page's machine state, so a fresh entry (skipping the
      // opening "click <nav>" step) is the coherent place to land.
      var t = this.remoteTours && this.remoteTours[b.flow];
      var resumeAt = (t && t.steps && t.steps.length > 1) ? 1 : 0;
      try { localStorage.setItem("jcs_tour_active", JSON.stringify({ flow: b.flow, step: resumeAt, t: Date.now() })); } catch (e) {}
      var here = location.pathname + location.search;
      if (b.url && b.url !== here) { try { location.assign(b.url); return; } catch (e) {} }
      this._maybeResume();   // already on that page → resume in place
    },
    // Which tour flows have an uploaded/recorded VIDEO (server source of truth) — so a
    // customer's nav hand-off can require a "ready" page (tour+video), matching the
    // catalog. Masters bypass this (preview). Loaded once tours are known.
    _loadVideoFlows() {
      var self = this;
      this._videoFlows = this._videoFlows || [];
      var flows = this.remoteTours ? Object.keys(this.remoteTours) : [];
      flows.forEach(function (flow) {
        if (!flow) return;
        feapi.guideVideoInfo(flow).then(function (info) {
          if (info && info.exists && self._videoFlows.indexOf(flow) < 0) self._videoFlows.push(flow);
        }).catch(function () {});
      });
    },
    // Persist the active guided tour + next step so a navigating click resumes it
    // on the next page. Only for real customer tours (not preview/auto).
    persistActive(step) {
      if (this.mode !== "guided") return;   // auto-demo doesn't persist
      // Persist for BOTH customer tours and the builder's Preview, flagging which
      // so the next page knows whether to reload the saved tour or the draft.
      try { localStorage.setItem("jcs_tour_active", JSON.stringify({ flow: this.flowKey, step: step, t: Date.now(), preview: !!this._isPreview, recording: !!this.recording })); } catch (e) {}
    },
    clearActive() {
      try { localStorage.removeItem("jcs_tour_active"); } catch (e) {}
      try { localStorage.removeItem("jcs_tour_step_urls"); } catch (e) {}
    },
    // ---- conditional steps ({showif:…}) ----
    // Should this step be shown for the CURRENT page state?
    _showIfPass(cond) {
      if (!cond || cond === "always") return true;
      if (cond === "hasdrawing") return this._hasDrawing();
      if (cond === "nodrawing") return !this._hasDrawing();
      if (cond === "hasassembly") return this._hasAssembly();
      if (cond === "noassembly") return !this._hasAssembly();
      return true;   // unknown condition → show (fail-open)
    },
    // Is the selected assembly's drawing on screen? The Search page only renders
    // <img alt="Selected assembly"> when the assembly actually has an image on file.
    _hasDrawing() {
      var el = document.querySelector('img[alt="Selected assembly"]');
      return !!(el && el.getAttribute("src") && el.offsetParent !== null);
    },
    // Does the selected machine have any assemblies to pick? The assembly dropdown
    // renders a selectable .item[data-value] per assembly (the help footer is
    // data-no-select), so zero of them = "no assemblies on this machine".
    _hasAssembly() {
      return document.querySelectorAll('#WearAssemblySelect .menu .item[data-value]').length > 0;
    },
    // First step index from `idx` (inclusive) stepping by `dir` whose show-if
    // condition passes, or -1 if none — lets the tour skip non-matching steps.
    _visibleFrom(idx, dir) {
      var n = this.steps.length;
      while (idx >= 0 && idx < n) {
        var st = this.steps[idx];
        if (this._showIfPass(st && st.showIf)) return idx;
        idx += dir;
      }
      return -1;
    },
    // Remember which URL the current step is shown on (persisted, so it survives
    // the reload a navigating step causes) → ‹ Back can tell whether the previous
    // step lived on a different page and do a real browser back to return there.
    _recordStepUrl() {
      try {
        var m = JSON.parse(localStorage.getItem("jcs_tour_step_urls") || "{}");
        m[this.stepIndex] = location.pathname + location.search;
        localStorage.setItem("jcs_tour_step_urls", JSON.stringify(m));
      } catch (e) {}
    },

    autoNext() {
      if (this.paused) return;
      if (this.isLast) this.exit(); else this.next();
    },
    next() {
      // If the narration is paused at a {wait}, "Skip"/Next RESUMES the voice
      // (speaks the rest), it does NOT advance the step.
      if (this._waitResume) { this._resolveWait(); return; }
      var ni = this._visibleFrom(this.stepIndex + 1, 1);   // skip show-if steps that don't match
      if (ni < 0) return this.exit();
      this.stepIndex = ni;
      this.paused = false;
      this.runStep();
    },
    // Go back a step (works in-page; the nav bar + page controls are global so
    // earlier targets are usually still present).
    prev() {
      var pi = this._visibleFrom(this.stepIndex - 1, -1);   // previous VISIBLE step (skips show-if-hidden)
      if (pi < 0) return;
      this.clearTimer(); this.stopAudio(); this.stopSpeak(); this.clearAdvanceClick();
      // If the PREVIOUS step was shown on a different page (we arrived here via a
      // navigating click), do a real browser BACK so that page returns, then resume
      // the tour at the previous step. Otherwise just step back in-place.
      var prevUrl = null;
      try { prevUrl = (JSON.parse(localStorage.getItem("jcs_tour_step_urls") || "{}"))[pi]; } catch (e) {}
      var here = location.pathname + location.search;
      if (prevUrl && prevUrl !== here) {
        this.persistActive(pi);   // resume at the prev step once the back-nav lands
        try { window.history.back(); return; } catch (e) {}
      }
      this.stepIndex = pi;
      this.paused = false;
      this.persistActive(this.stepIndex);
      this.runStep();
    },
    togglePause() {
      this.paused = !this.paused;
      if (this.paused) { this.stopAudio(); this.stopSpeak(); this.clearTimer(); }   // stopSpeak() stops the Web Audio clip (stopNeural) + any browser TTS
      else if (this._audio) { var p = this._audio.play(); if (p && p.catch) p.catch(function () {}); }
      else this.narrate(this.current, this.mode === "auto");
    },
    exit() {
      var endedAt = this.stepIndex;   // remember where the preview was when it ended
      this.teardown(); this.active = false;
      this.clearActive();   // a finished/exited tour shouldn't resume on the next page
      // If this run was the builder's "Preview tour", hand control back so the admin
      // keeps editing — and tell the builder which step the preview was on so it
      // scrolls back THERE (e.g. start a preview at step 10 → return to step 10),
      // instead of resetting to the top (step 1).
      if (this._isPreview) {
        var f = this.flowKey; this._isPreview = false;
        try { localStorage.setItem("jcs_tour_builder_focus_step", String(endedAt)); } catch (e) {}
        this.$root.$emit("open-tour-builder", f);
      }
    },

    // ---- positioning / helpers ----
    resolveTarget(target, timeoutMs) {
      var self = this;
      if (!target) return Promise.resolve(null);
      return new Promise(function (resolve) {
        var deadline = Date.now() + (timeoutMs || 1000);
        (function poll() {
          var el = self.looseQuery(target);
          if (el && self.isVisible(el)) return resolve(el);
          if (Date.now() > deadline) return resolve(el && self.isVisible(el) ? el : null);
          setTimeout(poll, 120);
        })();
      });
    },
    // Resolve a stored selector with RECOVERY: if the exact path no longer matches
    // (an old dropdown-inner selector after the value changed), fall back to its
    // leading STABLE token (#id / [attr] only — precise, never a bare class that
    // could match the wrong element). Heals saved steps so a highlight whose inner
    // node vanished still lands on its control instead of disappearing.
    looseQuery(sel) {
      if (!sel) return null;
      var el = null;
      try { el = document.querySelector(sel); } catch (e) {}
      if (!el) {
        var m = /^\s*(#[\w-]+|\[[^\]]+\])/.exec(sel);
        if (m && m[1]) { try { el = document.querySelector(m[1]); } catch (e) {} }
      }
      return el ? this.stableAnchor(el) : null;
    },
    // A diagram's <img>/hotspot layer scales + pans with zoom, so a highlight on it
    // drifts; anchor to the stable wrapper panel instead. (No-op for everything else.)
    stableAnchor(el) {
      if (!el || !el.closest) return el;
      var wrap = el.closest(".machine-image-wrap, .diagram-zoom-wrap, .callout-hotspot-layer");
      if (wrap) return wrap.closest(".machine-image-wrap") || wrap;
      if (el.tagName === "IMG") { var w = el.closest("figure, .ui.image, [class*='image-wrap']"); if (w) return w; }
      return el;
    },
    // Apply a saved box offset to the element's CURRENT rect, GUARDED: if the
    // offset would land the highlight far from the element (wrong element resolved
    // or a stale capture), discard it and outline the element — never fly off.
    boxedRect(R, box, pad) {
      if (box) {
        var rect = { left: R.left + box.dx, top: R.top + box.dy, width: box.w, height: box.h };
        var nearX = (rect.left < R.right + 220) && (rect.left + rect.width > R.left - 220);
        var nearY = (rect.top < R.bottom + 220) && (rect.top + rect.height > R.top - 220);
        if (nearX && nearY && rect.width > 2 && rect.height > 2) return rect;
      }
      return { left: R.left - pad, top: R.top - pad, width: R.width + pad * 2, height: R.height + pad * 2 };
    },
    isVisible(el) {
      if (!el) return false;
      var r = el.getBoundingClientRect();
      return r.width > 2 && r.height > 2;
    },
    scrollIntoView(el) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" }); } catch (e) {}
    },
    positionAt(el) {
      var R = el.getBoundingClientRect();
      var rect = this.boxedRect(R, this.current && this.current.targetBox, 8);
      this.spotStyle = {
        left: rect.left + "px", top: rect.top + "px",
        width: rect.width + "px", height: rect.height + "px", opacity: 1,
      };
      this.cursorStyle = { left: rect.left + rect.width / 2 + "px", top: rect.top + rect.height / 2 + "px" };
      this.placeCard({ left: rect.left, top: rect.top, bottom: rect.top + rect.height, right: rect.left + rect.width });
    },
    positionCenter() {
      var vw = window.innerWidth, vh = window.innerHeight;
      this.spotStyle = { left: vw / 2 + "px", top: vh / 2 + "px", width: "0px", height: "0px", opacity: 0 };
      this.cursorStyle = { left: vw / 2 + "px", top: vh / 2 + "px" };
      this.cardStyle = { left: "50%", top: "50%", transform: "translate(-50%,-50%)" };
    },
    drawRings(highlights) {
      var self = this;
      var rings = [];
      (highlights || []).forEach(function (h) {
        var sel = typeof h === "string" ? h : h.selector;
        var box = typeof h === "object" ? h.box : null;
        var el = self.looseQuery(sel);
        if (el) {
          var rect = self.boxedRect(el.getBoundingClientRect(), box, 4);
          // ringStyleFor returns left/top/width/height (so guardRects/placeCard still
          // read them) PLUS the admin-chosen border/fill/thickness/line.
          if (rect.width > 2 && rect.height > 2) rings.push(self.ringStyleFor(h, rect));
        }
      });
      this.rings = rings;
    },
    // ===== Highlight style (mirrors the builder's editor output) =====
    hlStyleOf(h) {
      var s = (h && typeof h === "object" && h.hlStyle) || {};
      return { border: s.border || "#c0392b", bg: s.bg || "", width: (s.width != null ? s.width : 2), line: s.line || "solid", anim: s.anim || "none", animCount: s.animCount || "infinite" };
    },
    _rgba(hex, a) {
      var m = /^#?([0-9a-fA-F]{6})$/.exec(hex || "");
      if (!m) return hex || "transparent";
      var n = parseInt(m[1], 16);
      return "rgba(" + ((n >> 16) & 255) + "," + ((n >> 8) & 255) + "," + (n & 255) + "," + a + ")";
    },
    ringStyleFor(h, rect) {
      var s = this.hlStyleOf(h);
      return {
        left: rect.left + "px", top: rect.top + "px", width: rect.width + "px", height: rect.height + "px",
        borderColor: s.border || "transparent",
        borderWidth: (s.width || 0) + "px",
        borderStyle: s.width > 0 ? s.line : "none",
        backgroundColor: s.bg ? this._rgba(s.bg, 0.3) : "transparent",
        color: s.border || "#c0392b",   // currentColor for the glow/sonar box-shadows
        anim: s.anim,                    // ignored as CSS by :style; read for the animation class
        animationIterationCount: String(s.animCount),   // overrides the class's "infinite" with the chosen count
      };
    },
    // Click-blockers over admin-masked elements (e.g. the cart icon) — fixed
    // overlays at each element's rect that swallow clicks so the customer can't
    // use them during this step. Re-measured on scroll/resize via reposition.
    computeMaskRects(masks) {
      var rects = [];
      (masks || []).forEach(function (m) {
        var sel = typeof m === "string" ? m : (m && m.selector);
        if (!sel) return;
        var els = [];
        try { els = document.querySelectorAll(sel); } catch (e) {}
        // Block EVERY matching element (all cart buttons, all item boxes…) — the
        // selector is broad on purpose, so one block covers them all.
        for (var i = 0; i < els.length; i++) {
          var R = els[i].getBoundingClientRect();
          if (R.width < 2 || R.height < 2) continue;
          var pad = 3;
          rects.push({ left: (R.left - pad) + "px", top: (R.top - pad) + "px", width: (R.width + pad * 2) + "px", height: (R.height + pad * 2) + "px" });
        }
      });
      return rects;
    },
    drawMasks(masks) { this.maskRects = this.computeMaskRects(masks); },
    // The results list reflows for reasons that fire NO scroll/resize event — the
    // filter narrows rows, thumbnails finish loading and grow row heights, the
    // "clear your filter" notice appears, a collapsed sub-assembly expands — so a
    // one-shot drawMasks at step start leaves the fixed overlays misaligned (or
    // missing for icons that appear later). Poll the live DOM while the step is
    // active and re-measure ALL matches (re-querySelectorAll, so new cart icons get
    // covered too). Cheap — a handful of overlays; only reassign when they change.
    startMaskTracking() {
      var self = this;
      this.clearMaskTimer();
      // Re-anchor EVERYTHING (spotlight, rings, masks, click-guard) on a short poll
      // while the step is active — not just masks. The list reflows for reasons that
      // fire NO scroll/resize event (filter narrows rows, thumbnails load and grow
      // rows, the "clear filter" notice appears, a sub-assembly expands, a balloon
      // pick floats a row to the top), and the target's DOM node can even be replaced
      // on a re-render. reposition() re-measures + re-resolves a detached target, so
      // the highlight stays glued instead of drifting. Cheap + idempotent.
      var tick = function () {
        if (!self.active) { self._maskTimer = null; return; }
        self.reposition();
        self._maskTimer = setTimeout(tick, 250);
      };
      tick();
    },
    clearMaskTimer() { if (this._maskTimer) { clearTimeout(this._maskTimer); this._maskTimer = null; } },
    // Place the narration card so it clears BOTH the primary spotlight and every
    // extra highlight, and is NEVER off-screen. Try a set of anchors around the
    // focus rect (below / right / left / above) and the screen corners; clamp each
    // FULLY on-screen, then score it by how much it overlaps the highlights. The
    // first anchor that clears them all wins; otherwise the least-overlapping (still
    // on-screen) one does. Replaces the old logic that anchored above a near-top
    // highlight with no clamp and ran off the top of the page.
    placeCard(r) {
      var vh = window.innerHeight, vw = window.innerWidth, gap = 16, margin = 12, cardW = 360;
      var cardH = Math.min(this._cardHeight() || 220, vh - margin * 2);
      var occ = this._highlightRects();
      var anchors = [
        { l: r.left, t: r.bottom + gap },                    // below, aligned to the highlight
        { l: r.right + gap, t: r.top },                       // to its right
        { l: r.left - cardW - gap, t: r.top },                // to its left
        { l: r.left, t: r.top - cardH - gap },                // above
        { l: vw - cardW - margin, t: r.bottom + gap },        // below, far right
        { l: vw - cardW - margin, t: margin },                // top-right corner
        { l: margin, t: vh - cardH - margin },                // bottom-left corner
        { l: vw - cardW - margin, t: vh - cardH - margin },   // bottom-right corner
      ];
      var best = null;
      for (var i = 0; i < anchors.length; i++) {
        var l = Math.min(Math.max(margin, anchors[i].l), Math.max(margin, vw - cardW - margin));
        var t = Math.min(Math.max(margin, anchors[i].t), Math.max(margin, vh - cardH - margin));
        var box = { left: l, top: t, right: l + cardW, bottom: t + cardH };
        var ov = 0;
        for (var k = 0; k < occ.length; k++) ov += this._overlapArea(box, occ[k]);
        if (ov === 0) { best = { l: l, t: t }; break; }
        if (!best || ov < best.ov) best = { l: l, t: t, ov: ov };
      }
      this.cardStyle = { left: best.l + "px", top: best.t + "px", transform: "none" };
    },
    // Measured height of the rendered card (0 if not yet on screen → caller defaults).
    _cardHeight() {
      try { var c = this.$el && this.$el.querySelector(".gtour-card"); return c ? c.getBoundingClientRect().height : 0; } catch (e) { return 0; }
    },
    // Every on-screen highlight rect the card must avoid: the primary spotlight (when
    // visible) + each extra highlight ring.
    _highlightRects() {
      var rects = [];
      var s = this.spotStyle || {};
      var w = parseFloat(s.width), h = parseFloat(s.height);
      if (s.opacity !== 0 && w > 1 && h > 1) {
        var l = parseFloat(s.left), t = parseFloat(s.top);
        rects.push({ left: l, top: t, right: l + w, bottom: t + h });
      }
      (this.rings || []).forEach(function (rg) {
        var rl = parseFloat(rg.left), rt = parseFloat(rg.top), rw = parseFloat(rg.width), rh = parseFloat(rg.height);
        if (rw > 1 && rh > 1) rects.push({ left: rl, top: rt, right: rl + rw, bottom: rt + rh });
      });
      return rects;
    },
    _overlapArea(a, b) {
      var x = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
      var y = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
      return x * y;
    },
    // True while the page's Add-to-Cart modal is on screen — that makes the
    // current guided step a "fill in the modal" step (wait for Add to Cart, not a
    // raw click), and keeps the narration card docked beside it.
    isCartModalOpen() {
      try {
        var m = document.querySelector("#addToCartModal, .add-to-cart-modal");
        if (!m) return false;
        var cs = window.getComputedStyle(m);
        if (cs.display === "none" || cs.visibility === "hidden") return false;
        return this.isVisible(m);
      } catch (e) { return false; }
    },
    // Dock the narration card beside the (centered, raised) Add-to-Cart modal so
    // the step text stays visible instead of hiding behind it. Prefer the right of
    // the modal, then the left, then fall back to the normal placement.
    placeCardBesideModal() {
      var dlg = null;
      try { dlg = document.querySelector("#addToCartModal .modal-dialog, .add-to-cart-modal .modal-dialog"); } catch (e) {}
      if (!dlg) return this.positionCenter();
      var R = dlg.getBoundingClientRect(), vw = window.innerWidth, cardW = 360, gap = 16;
      var top = Math.max(12, R.top);
      if (R.right + gap + cardW < vw - 8) this.cardStyle = { left: (R.right + gap) + "px", top: top + "px", transform: "none" };
      else if (R.left - gap - cardW > 8) this.cardStyle = { left: (R.left - gap - cardW) + "px", top: top + "px", transform: "none" };
      else this.placeCard({ left: R.left, top: R.top, bottom: R.bottom, right: R.right });
    },
    reposition() {
      if (!this.active) return;
      this.reflowTick++;   // re-measure the click-guard mat against the new viewport/scroll
      var step = this.current;
      // If the target's DOM node detached (the list re-rendered, a row floated to
      // the top, the dropdown rebuilt), re-resolve it so the spotlight follows the
      // element to its new node instead of sticking to a stale/hidden one.
      if (step && step.target && !this._openMenuEl && !this._inputResultsEl &&
          (!this._targetEl || !this.isVisible(this._targetEl))) {
        var re = this.looseQuery(step.target);
        if (re && this.isVisible(re)) this._targetEl = re;
      }
      // While a dropdown's menu is open, keep the spotlight wrapping the menu;
      // an input step's spotlight wraps the field + its results; else the target.
      if (this._openMenuEl && this.isVisible(this._openMenuEl)) {
        this.paintMenuSpot(this._openMenuEl);
      } else if (!this.isDropdownStep() && !this.isDiagramStep() && this.isInputStep()) {
        // Input/search step: re-detect an OPEN suggestions dropdown LIVE so the
        // spotlight grows the moment async autocomplete renders (no keystroke
        // lands it) and shrinks back to the field when it closes — making the
        // search box behave like the machine/assembly dropdowns. The part-filter's
        // inline list still grows only after the customer types (tracked via
        // _inputResultsEl, set on input), so that step is unchanged.
        var sug = this.findInputSuggestions();
        if (sug) { this._inputResultsEl = sug; this.paintUnionSpot(this._targetEl, sug); }
        else if (this._inputResultsEl && this.isVisible(this._inputResultsEl)) this.paintUnionSpot(this._targetEl, this._inputResultsEl);
        else if (this._targetEl && this.isVisible(this._targetEl)) this.positionAt(this._targetEl);
      } else if (this._inputResultsEl && this.isVisible(this._inputResultsEl)) {
        this.paintUnionSpot(this._targetEl, this._inputResultsEl);
      } else if (this._targetEl && this.isVisible(this._targetEl)) {
        // Cart-expand step: keep the whole tray (order + quotes) wrapped on scroll/resize
        // so it stays consistent with the live grow-poll; else just the target.
        var cg = this.isCartGrowStep() ? this.cartGrowEl() : null;
        if (cg) this.paintRectSpot(cg.getBoundingClientRect());
        else this.positionAt(this._targetEl);
      }
      if (this._modalStep && this.isCartModalOpen()) this.placeCardBesideModal();
      if (step) { this.drawRings(step.highlights); this.drawMasks(step.masks); }
    },
    animateClick(el, fire) {
      var self = this;
      return new Promise(function (resolve) {
        self.clicking = true;
        setTimeout(function () {
          if (fire && el) try { el.click(); } catch (e) {}
          self.clicking = false;
          setTimeout(resolve, fire ? 400 : 150);
        }, 450);
      });
    },
    showToast(el) {
      this.toast = this.current.toast || "Demo only";
      if (el) {
        var r = el.getBoundingClientRect();
        this.toastStyle = { left: Math.min(r.left, window.innerWidth - 280) + "px", top: Math.max(12, r.top - 44) + "px" };
      } else this.toastStyle = { left: "50%", top: "18%", transform: "translateX(-50%)" };
    },
    delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); },
    clearTimer() { if (this._timer) { clearTimeout(this._timer); this._timer = null; } },
    clearAdvanceClick() {
      if (this._advanceClick) { document.removeEventListener("click", this._advanceClick, true); this._advanceClick = null; }
      if (this._advanceChange) { document.removeEventListener("change", this._advanceChange, true); this._advanceChange = null; }
      if (this._onTypeInput) { document.removeEventListener("input", this._onTypeInput, true); this._onTypeInput = null; }
      if (this._resultTimer) { clearTimeout(this._resultTimer); this._resultTimer = null; }
      if (this._cartOpenWatch) { clearInterval(this._cartOpenWatch); this._cartOpenWatch = null; }
      this._clearWaitListener();
      this._waitResume = null;
      this.waitHint = false;
      this.clearMenuTimer();
      this.clearGrowTimer();
      this.clearMaskTimer();
      this._openMenuEl = null;
      this._inputResultsEl = null;
    },
    stopAudio() {
      if (this._audio) { try { this._audio._teardown = true; this._audio.pause(); this._audio.src = ""; } catch (e) {} this._audio = null; }
    },
    stopSpeak() {
      try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (e) {}
      this.stopNeural();
    },
    // Azure NEURAL voice playback — the same path the builder previews use, so the
    // tour speaks in the admin's chosen voice (jcs_tour_voice) instead of the flat
    // browser voice. Fetch the mp3 and play it through Web Audio (immune to the
    // <audio> autoplay block once the ctx is resumed on the tour-start click).
    // Falls back to the browser voice if the endpoint or the chosen voice is
    // unavailable (one failed fetch flips _neuralDead so we don't retry per step).
    ensureAudioCtx() {
      try {
        if (!this._actx) { var AC = window.AudioContext || window.webkitAudioContext; if (AC) this._actx = new AC(); }
        if (this._actx && this._actx.state === "suspended" && this._actx.resume) this._actx.resume();
      } catch (e) {}
      return this._actx || null;
    },
    neuralSpeak(text, onEnd, onFail) {
      var self = this;
      if (this._neuralDead || this.paused) { onFail(); return; }
      var ctx = this.ensureAudioCtx();
      if (!ctx) { this._neuralDead = true; onFail(); return; }
      var saved = "";
      try { saved = localStorage.getItem("jcs_tour_voice") || ""; } catch (e) {}
      // _ttsVoice pins the voice we've already proven works THIS run (set by the
      // fallback below), so a step doesn't re-hit an unavailable saved voice.
      var voice = this._ttsVoice || saved;
      var myStep = this.stepIndex;
      var FALLBACK = "en-US-AriaNeural";   // universally-available neural voice (same one the audio self-test uses)
      var playBlob = function (blob) {
        return blob.arrayBuffer().then(function (ab) {
          return ctx.decodeAudioData(ab.slice(0)).then(function (buf) {
            if (!self.active || self.stepIndex !== myStep || self.paused) return; // step changed while fetching
            var go = function () {
              self.stopNeural();
              var src = ctx.createBufferSource();
              src.buffer = buf; src.connect(ctx.destination);
              src.onended = function () { if (self._curSrc === src) { self._curSrc = null; onEnd(); } };
              self._curSrc = src;
              src.start(0);
            };
            // Resume the context BEFORE playing (mirrors the working self-test; a
            // fire-and-forget resume can leave it suspended at play time → silent).
            if (ctx.state === "suspended" && ctx.resume) ctx.resume().then(go, go);
            else go();
          });
        });
      };
      feapi.ttsPreview(text, voice).then(function (blob) {
        if (blob) return playBlob(blob);
        // The chosen voice synthesized NO audio (not available on this Azure
        // resource/tier/region — e.g. a newer voice on the Free F0 tier). Retry ONCE
        // with the default neural voice before giving up, so a single bad saved voice
        // can't silence the whole tour. Still neural — never the flat browser voice.
        if (voice && voice.toLowerCase() !== FALLBACK.toLowerCase()) {
          self._ttsVoice = FALLBACK;       // use the working voice for the rest of this run
          return feapi.ttsPreview(text, FALLBACK).then(function (b2) {
            if (b2) return playBlob(b2);
            self._neuralDead = true; onFail(); return null;   // even the default failed → endpoint/Azure down → silent
          });
        }
        self._neuralDead = true; onFail(); return null;
      }).catch(function () { onFail(); });
    },
    // Play a recorded / pre-synthesized clip URL (blob: for a preview tour, or the
    // anonymous /audio mp3 for a saved tour) through WEB AUDIO — NOT a raw <audio>
    // element, which Chrome's autoplay policy can block, spuriously dropping us to
    // the browser voice (the "Andrew AND the browser both play" double). Mirrors
    // neuralSpeak / the builder's playUrl; the browser voice now fires ONLY on a
    // genuine fetch/decode failure (onFail). Stopping is via stopNeural().
    playClipUrl(url, onEnd, onFail) {
      var self = this;
      if (this.paused) return;
      var ctx = this.ensureAudioCtx();
      if (!ctx) { onFail(); return; }
      var myStep = this.stepIndex;
      fetch(url).then(function (r) { return r.arrayBuffer(); })
        .then(function (ab) { return ctx.decodeAudioData(ab.slice(0)); })
        .then(function (buf) {
          if (!self.active || self.stepIndex !== myStep || self.paused) return; // step changed while fetching
          var go = function () {
            self.stopNeural();
            var src = ctx.createBufferSource();
            src.buffer = buf; src.connect(ctx.destination);
            src.onended = function () { if (self._curSrc === src) { self._curSrc = null; onEnd(); } };
            self._curSrc = src;
            src.start(0);
          };
          // Resume the context BEFORE playing (the working self-test awaits resume;
          // a fire-and-forget resume can leave it suspended at play time → silent).
          if (ctx.state === "suspended" && ctx.resume) ctx.resume().then(go, go);
          else go();
        })
        .catch(function () { onFail(); });
    },
    stopNeural() {
      if (this._curSrc) { try { this._curSrc.onended = null; this._curSrc.stop(0); } catch (e) {} this._curSrc = null; }
    },
    teardown() {
      try { document.body.classList.remove("gtour-active"); } catch (e) {}
      if (this._handoffClickBound) { try { document.removeEventListener("click", this._handoffClickBound, true); } catch (e) {} this._handoffClickBound = null; }
      if (this._recNavGuard) { try { document.removeEventListener("click", this._recNavGuard, true); } catch (e) {} this._recNavGuard = null; }
      if (this._recUnloadGuard) { try { window.removeEventListener("beforeunload", this._recUnloadGuard); } catch (e) {} this._recUnloadGuard = null; }
      this.clearTimer();
      this.stopAudio();
      this.stopSpeak();
      this.clearAdvanceClick();
      this.toast = "";
      this.awaitingClick = false;
      this.maskRects = [];
      this._targetEl = null;
      if (this._onReflow) {
        window.removeEventListener("scroll", this._onReflow, true);
        window.removeEventListener("resize", this._onReflow);
        this._onReflow = null;
      }
      if (this._onKey) { window.removeEventListener("keydown", this._onKey, true); this._onKey = null; }
      if (this._onRecMove) { window.removeEventListener("mousemove", this._onRecMove, true); this._onRecMove = null; }
      if (this._onRecDown) { window.removeEventListener("mousedown", this._onRecDown, true); this._onRecDown = null; }
      if (this._onRecUp) { window.removeEventListener("mouseup", this._onRecUp, true); this._onRecUp = null; }
      this.recording = false; this.recClicking = false;
    },
  },
};
</script>

<style>
.gtour-catcher { position: fixed; inset: 0; z-index: 20000; background: transparent; cursor: default; }
/* GUIDED click-guard strips — block clicks everywhere except the spotlight hole
   (below the spotlight ring z20001, the masks z20002 + the card z20005, above the
   page). Transparent: the dim is the spotlight's box-shadow. */
.gtour-guard { position: fixed; z-index: 20000; background: transparent; pointer-events: auto; cursor: default; }
.gtour-spot {
  position: fixed; z-index: 20001; border-radius: 10px;
  box-shadow: 0 0 0 9999px rgba(17, 17, 17, 0.6);
  border: 2px solid #c0392b; pointer-events: none;
  transition: left 0.45s ease, top 0.45s ease, width 0.45s ease, height 0.45s ease, opacity 0.3s ease;
}
/* GUIDED mode: paint the dim/privacy layer on the guard strips, which already tile
   the viewport AROUND every hole (the primary spotlight AND each extra highlight).
   That makes every highlight — primary or secondary — an undimmed hole with its own
   red ring, under ONE uniform dim (no double-dimming). The spotlight then drops its
   own box-shadow dim, which would otherwise stack on top of the guard dim. (AUTO
   mode keeps the box-shadow dim — it has no guard strips and never has a 2nd hole.) */
.gtour-guided .gtour-guard { background: rgba(17, 17, 17, 0.6); }
.gtour-guided .gtour-spot { box-shadow: none; }
.gtour-ring {
  /* Extra highlights match the primary spotlight's design (.gtour-spot): same
     red border + radius, fully TRANSPARENT inside (no fill) so what's behind is
     unobscured — without the page-dimming shadow (only one spotlight). */
  position: fixed; z-index: 20002; border-radius: 10px;
  border: 2px solid #c0392b; background: transparent;
  pointer-events: none; transition: all 0.3s ease;
}
/* Highlight ANIMATIONS in the live tour (admin-chosen per highlight). currentColor =
   the highlight's border color (set inline) so glow/sonar match the chosen color. */
.gtour-ring.gtour-anim-glow   { animation: gtourA_glow 1.5s ease-in-out infinite; }
.gtour-ring.gtour-anim-sonar  { animation: gtourA_sonar 1.5s ease-out infinite; }
.gtour-ring.gtour-anim-pulse  { animation: gtourA_pulse 1.2s ease-in-out infinite; }
.gtour-ring.gtour-anim-blink  { animation: gtourA_blink 1s ease-in-out infinite; }
.gtour-ring.gtour-anim-bounce { animation: gtourA_bounce 1s ease-in-out infinite; }
.gtour-ring.gtour-anim-shake  { animation: gtourA_shake 0.9s ease-in-out infinite; }
@keyframes gtourA_glow   { 0%,100% { box-shadow: 0 0 2px 0 currentColor; } 50% { box-shadow: 0 0 14px 3px currentColor; } }
@keyframes gtourA_sonar  { 0% { box-shadow: 0 0 0 0 currentColor; } 70% { box-shadow: 0 0 0 14px transparent; } 100% { box-shadow: 0 0 0 0 transparent; } }
@keyframes gtourA_pulse  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
@keyframes gtourA_blink  { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
@keyframes gtourA_bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
@keyframes gtourA_shake  { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-3px); } 75% { transform: translateX(3px); } }
@media (prefers-reduced-motion: reduce) {
  .gtour-ring.gtour-anim-glow,.gtour-ring.gtour-anim-sonar,.gtour-ring.gtour-anim-pulse,.gtour-ring.gtour-anim-blink,.gtour-ring.gtour-anim-bounce,.gtour-ring.gtour-anim-shake { animation: none; }
}
/* Click-blocker over an admin-masked element — sits ABOVE the dim + the element,
   swallows clicks, and reads as disabled (not-allowed cursor). */
.gtour-mask {
  position: fixed; z-index: 20002; border-radius: 6px;
  background: rgba(70, 70, 70, 0.3); cursor: not-allowed; pointer-events: auto;
}
.gtour-clickhint {
  position: fixed; z-index: 20002; border-radius: 12px; pointer-events: none;
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.55); animation: gtourClick 1.2s ease-out infinite;
}
@keyframes gtourClick {
  0% { box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.7); }
  70% { box-shadow: 0 0 0 12px rgba(192, 57, 43, 0); }
  100% { box-shadow: 0 0 0 2px rgba(192, 57, 43, 0); }
}
.gtour-cursor {
  position: fixed; z-index: 20003; margin: -4px 0 0 -4px; pointer-events: none;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.4));
  transition: left 0.55s cubic-bezier(0.4, 0, 0.2, 1), top 0.55s cubic-bezier(0.4, 0, 0.2, 1);
}
.gtour-cursor.is-click::after {
  content: ""; position: absolute; left: 2px; top: 2px; width: 26px; height: 26px;
  margin: -13px 0 0 -13px; border-radius: 50%; background: rgba(192, 57, 43, 0.35);
  animation: gtourPulse 0.5s ease-out;
}
@keyframes gtourPulse { 0% { transform: scale(0.2); opacity: 0.9; } 100% { transform: scale(2.4); opacity: 0; } }
/* Standalone heads-up after a nav hand-off to a page with no tour — centered near the
   top, above the page; auto-dismisses (navNote is cleared on a timer). */
.gtour-navnote {
  position: fixed; z-index: 20050; top: 86px; left: 50%; transform: translateX(-50%);
  max-width: min(520px, 94vw); background: #1d1d1d; color: #fff;
  font-size: 13px; font-weight: 600; line-height: 1.4;
  padding: 10px 14px; border-radius: 10px; box-shadow: 0 10px 34px rgba(0, 0, 0, 0.4);
  pointer-events: auto;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; justify-content: center;
}
.gtour-navnote-msg { flex: 0 1 auto; }
.gtour-navnote-back { flex: 0 0 auto; background: #c0392b; color: #fff; border: 0; border-radius: 7px; padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer; white-space: nowrap; }
.gtour-navnote-back:hover { background: #a02b1f; }
.gtour-navnote-x { flex: 0 0 auto; background: transparent; color: #bbb; border: 0; font-size: 14px; line-height: 1; cursor: pointer; padding: 2px 4px; }
.gtour-navnote-x:hover { color: #fff; }
/* Red ring tracking the REAL cursor during a recorded Preview Tour. Above every
   overlay (z 20006) so it's always visible in the capture; pointer-events:none so
   it never blocks the clicks the presenter is making. Moved via inline left/top. */
.gtour-rec-cursor {
  position: fixed; top: 0; left: 0; z-index: 20006; width: 30px; height: 30px;
  border-radius: 50%; border: 3px solid #e02b1b; background: rgba(224, 43, 27, 0.12);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.55), 0 0 11px rgba(224, 43, 27, 0.6);
  transform: translate(-50%, -50%); pointer-events: none;
  transition: left 0.04s linear, top 0.04s linear, transform 0.12s ease, background 0.12s ease;
}
.gtour-rec-cursor.is-click {
  transform: translate(-50%, -50%) scale(0.66);
  background: rgba(224, 43, 27, 0.34);
}
.gtour-toast {
  position: fixed; z-index: 20004; background: #2a2a2a; color: #fff; font-size: 12.5px;
  font-weight: 600; padding: 7px 12px; border-radius: 6px; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
  pointer-events: none; max-width: 270px;
}
.gtour-card {
  position: fixed; z-index: 20005; width: 360px; max-width: calc(100vw - 24px);
  background: #fff; border-radius: 10px; border-top: 3px solid #c0392b;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.28); padding: 14px 16px 12px;
  transition: left 0.4s ease, top 0.4s ease;
}
/* During a guided tour the page's Add-to-Cart modal is PART of the walkthrough
   (the "add to cart → PO# + quantity → Add to Cart" step). By default a Bootstrap
   modal sits at z~1050, far below the tour's overlays (guard/spot/card 20000-20005),
   so it would open hidden behind them. Raise it (and its backdrop) above the tour.
   The backdrop stays BELOW the narration card (z20005) so the step text remains
   readable; the modal dialog goes ABOVE the card so it's fully clickable. */
body.gtour-active .modal-backdrop { z-index: 20003 !important; }
body.gtour-active #addToCartModal,
body.gtour-active .modal.add-to-cart-modal { z-index: 20009 !important; }
.gtour-card-close {
  position: absolute; top: 6px; right: 7px; width: 30px; height: 30px; padding: 0;
  border: 1px solid #e2c4c0 !important; background: #f7eceb !important; color: #c0392b !important;
  font-size: 16px; line-height: 26px; text-align: center; cursor: pointer; border-radius: 50%; font-weight: 800;
}
.gtour-card-close:hover { background: #c0392b !important; color: #fff !important; }
.gtour-card-step {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: #c0392b; margin-bottom: 5px; padding-right: 24px;
}
.gtour-card-text { font-size: 14px; line-height: 1.45; color: #2a2a2a; }
.gtour-card-text b { color: #111; }
.gtour-card-cue {
  margin-top: 8px; font-size: 12.5px; font-weight: 700; color: #b9770e;
  background: #fff4e0; border: 1px solid #f0c36d; border-radius: 6px; padding: 5px 9px;
}
.gtour-card-result {
  margin-top: 8px; font-size: 12.5px; font-weight: 600; color: #1e7a34;
  background: #eafbef; border: 1px solid #b6e4c2; border-radius: 6px; padding: 6px 9px;
}
.gtour-card-result b { color: #14622a; }
.gtour-card-controls { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
.gtour-spacer { flex: 1 1 auto; }
.gtour-btn {
  border: 0; border-radius: 6px; background: #c0392b; color: #fff !important; font-size: 12.5px;
  font-weight: 700; padding: 7px 14px; cursor: pointer;
}
.gtour-btn:hover { background: #a02b1f; }
.gtour-btn-ghost { background: none; color: #777 !important; padding: 7px 8px; font-weight: 600; }
.gtour-btn-ghost:hover { background: #f2f2f2; color: #333 !important; }
</style>
